import React, { useState, useEffect, useContext } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Modal, Form, Spinner, Badge, Row, Col, Card, Alert, Tabs, Tab, Table, InputGroup, Nav } from 'react-bootstrap';
import DataTable from 'react-data-table-component';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../../api/axios';
import { ThemeContext } from '../../context/ThemeContext';
import { LoadingState } from '../../components/common/LoadingState';
import getDataTableStyles from '../../utils/tableStyles';
import CustomerApiManager from '../../api/services/CustomerApiManager';
import UserApiManager from '../../api/services/UserApiManager';
import { CUSTOMER_STATUS } from '../../constants/customerStatuses';

const getCustomStyles = (isDark) => ({
  headRow: {
    style: {
      backgroundColor: '#183eeb',
      minHeight: '48px',
      borderTopLeftRadius: '8px',
      borderTopRightRadius: '8px',
    },
  },
  headCells: {
    style: {
      backgroundColor: '#183eeb',
      color: '#ffffff',
      fontWeight: '700',
      textTransform: 'uppercase',
      paddingLeft: '14px',
      paddingRight: '14px',
      fontSize: '0.78rem',
      letterSpacing: '0.5px',
      border: 'none',
    },
  },
  rows: {
    style: {
      minHeight: '64px',
      backgroundColor: isDark ? '#161e38' : '#ffffff',
      color: isDark ? '#ffffff' : '#010028',
      paddingTop: '6px',
      paddingBottom: '6px',
      '&:hover': {
        backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(24, 62, 235, 0.04)',
      },
    },
  },
  cells: {
    style: {
      paddingLeft: '14px',
      paddingRight: '14px',
      border: 'none',
      color: isDark ? '#ffffff' : '#010028',
    },
  },
  table: {
    style: {
      backgroundColor: isDark ? '#161e38' : '#ffffff',
      borderCollapse: 'collapse',
      border: 'none',
      width: '100%',
    },
  },
  tableWrapper: {
    style: {
      backgroundColor: isDark ? '#161e38' : '#ffffff',
    },
  },
  progress: {
    style: {
      backgroundColor: isDark ? '#161e38' : '#ffffff',
      color: isDark ? '#60a5fa' : '#183eeb',
    },
  },
  pagination: {
    style: {
      backgroundColor: isDark ? '#161e38' : '#ffffff',
      color: isDark ? '#ffffff' : '#010028',
    },
  },
  noData: {
    style: {
      backgroundColor: isDark ? '#161e38' : '#ffffff',
      color: isDark ? '#94a3b8' : '#64748b',
    },
  },
});

const CANAL_CONFIG = {
  transformadores: {
    key: 'transformadores',
    labelKey: 'canal_transformadores',
    badgeKey: 'canal_transformadores_badge',
    label: 'Transformadores (Food Service)',
    badgeLabel: 'Transformadores ($500)',
    limite: 500,
    dias_credito: 8,
    color: '#ea580c',
    vistaKey: 'catalog_view_industrial',
    vista: 'Presentaciones Industriales / Bloques',
    subcategories: [
      { id: 'restaurantes', key: 'subcat_restaurantes', defaultLabel: 'Restaurantes' },
      { id: 'panaderias_pastelerias', key: 'subcat_panaderias_pastelerias', defaultLabel: 'Panaderías / Pastelerías' },
      { id: 'pizzerias', key: 'subcat_pizzerias', defaultLabel: 'Pizzerías' },
      { id: 'hoteles', key: 'subcat_hoteles', defaultLabel: 'Hoteles' },
      { id: 'food_service', key: 'subcat_food_service', defaultLabel: 'Food Service' },
    ]
  },
  supermercados_bodegones: {
    key: 'supermercados_bodegones',
    labelKey: 'canal_supermercados_bodegones',
    badgeKey: 'canal_retail_badge',
    label: 'Supermercados y Bodegones (Retail)',
    badgeLabel: 'Retail ($1.200)',
    limite: 1200,
    dias_credito: 8,
    color: '#1d4ed8',
    vistaKey: 'catalog_view_retail',
    vista: 'Consumo Masivo / Retail',
    subcategories: [
      { id: 'supermercados', key: 'subcat_supermercados', defaultLabel: 'Supermercados' },
      { id: 'hipermercados', key: 'subcat_hipermercados', defaultLabel: 'Hipermercados' },
      { id: 'bodegones', key: 'subcat_bodegones', defaultLabel: 'Bodegones' },
    ]
  },
  tiendas_bodegas: {
    key: 'tiendas_bodegas',
    labelKey: 'canal_tiendas_bodegas',
    badgeKey: 'canal_tradicional_badge',
    label: 'Tiendas y Bodegas (Tradicional)',
    badgeLabel: 'Tradicional ($300)',
    limite: 300,
    dias_credito: 8,
    color: '#059669',
    vistaKey: 'catalog_view_traditional',
    vista: 'Consumo Masivo / Tradicional',
    subcategories: [
      { id: 'abastos', key: 'subcat_abastos', defaultLabel: 'Abastos' },
      { id: 'tiendas_conveniencia', key: 'subcat_tiendas_conveniencia', defaultLabel: 'Tiendas de conveniencia' },
    ]
  }
};

const getCanalLabel = (canal, t) => {
  const cfg = CANAL_CONFIG[canal];
  if (!cfg) return canal || '';
  return t ? t(cfg.labelKey, cfg.label) : cfg.label;
};

const getCanalBadgeLabel = (canal, t) => {
  const cfg = CANAL_CONFIG[canal];
  if (!cfg) return canal || '';
  return t ? t(cfg.badgeKey, cfg.badgeLabel) : cfg.badgeLabel;
};

const getSubcatLabel = (canal, subId, t) => {
  if (!subId) return '';
  const cfg = CANAL_CONFIG[canal];
  const sub = cfg?.subcategories?.find(s => s.id === subId || s.defaultLabel === subId);
  if (sub) {
    return t ? t(sub.key, sub.defaultLabel) : sub.defaultLabel;
  }
  // Try searching in any canal
  for (const c of Object.values(CANAL_CONFIG)) {
    const found = c.subcategories.find(s => s.id === subId || s.defaultLabel === subId);
    if (found) {
      return t ? t(found.key, found.defaultLabel) : found.defaultLabel;
    }
  }
  return subId;
};

const getCatalogViewLabel = (canal, t) => {
  const cfg = CANAL_CONFIG[canal];
  if (!cfg) return t ? t('catalog_view_retail', 'Consumo Masivo / Retail') : 'Consumo Masivo / Retail';
  return t ? t(cfg.vistaKey, cfg.vista) : cfg.vista;
};

function Customerlisting() {
  const { t } = useTranslation();
  const { isDark } = useContext(ThemeContext) || {};
  const customStyles = getDataTableStyles(isDark);

  // Primary Data State
  const [customers, setCustomers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [zones, setZones] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, requiresVisit: 0, active: 0, disabled: 0 });
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  // Navigation Tabs filter
  const [activeMainTab, setActiveMainTab] = useState('all');

  // Search & Filter state
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [zoneFilter, setZoneFilter] = useState(searchParams.get('zone_id') || '');
  const [rubroFilter, setRubroFilter] = useState('');
  const [canalFilter, setCanalFilter] = useState('');
  const [channelConfig, setChannelConfig] = useState(CANAL_CONFIG);
  const getCanalCfg = (canal) => channelConfig[canal] || CANAL_CONFIG[canal];

  // Modals state
  const [showModal, setShowModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [editingId, setEditingId] = useState(null);

  // Inspection Modal (for pending_approval or requires_visit)
  const [showInspectModal, setShowInspectModal] = useState(false);
  const [inspectCustomer, setInspectCustomer] = useState(null);
  const [customerAddresses, setCustomerAddresses] = useState([]);
  const [selectedAddressIndex, setSelectedAddressIndex] = useState(0);

  // Action Modals
  const [showVisitModal, setShowVisitModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);

  // Notification & Validation states
  const [validationError, setValidationError] = useState('');
  const [invalidFields, setInvalidFields] = useState({});
  const [successNotification, setSuccessNotification] = useState('');

  // Bulk load error report state (HU09)
  const [importErrors, setImportErrors] = useState([]);
  const [showImportErrorModal, setShowImportErrorModal] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  // Document attachments state
  const [documentFiles, setDocumentFiles] = useState([]);
  const [existingDocuments, setExistingDocuments] = useState([]);

  // Form States
  const initialForm = {
    rif_cedula: '',
    razon_social: '',
    nombre_comercial: '',
    nombre_contacto: '',
    direccion_fisica: '',
    latitude: '',
    longitude: '',
    ubicacion_gps: '',
    email_facturacion: '',
    telefono: '',
    canal_comercial: '',
    subcategoria_comercial: '',
    categoria: '',
    segmentacion: '',
    limite_credito: 0,
    dias_gracia: 8,
    zone_id: '',
    vendedor_id: ''
  };
  const [formData, setFormData] = useState(initialForm);
  const [importFile, setImportFile] = useState(null);
  const [modalTab, setModalTab] = useState('manual');

  const [visitData, setVisitData] = useState({ vendedor_id: '', zone_id: '', notas: '' });
  const [approveData, setApproveData] = useState({ limite_credito: 0, dias_gracia: 8, vendedor_id: '', zone_id: '' });
  const [rejectData, setRejectData] = useState({ motivo: '' });
  const [previewImage, setPreviewImage] = useState(null);

  // Fetch Customers
  const fetchCustomers = async () => {
    setLoading(true);
    try {
      let response;
      if (CustomerApiManager && typeof CustomerApiManager.getCustomers === 'function') {
        response = await CustomerApiManager.getCustomers({ search, estatus: statusFilter, zone_id: zoneFilter, rubro: rubroFilter, canal_comercial: canalFilter, per_page: 2500 });
      } else {
        response = await axiosInstance.get('/admin/customers', { params: { search, estatus: statusFilter, zone_id: zoneFilter, rubro: rubroFilter, canal_comercial: canalFilter, per_page: 2500 } });
      }

      if (response && response.success) {
        const list = response.data?.data || response.data || [];
        setCustomers(Array.isArray(list) ? list : []);
        if (response.stats || response.data?.stats) {
          setStats(response.stats || response.data?.stats);
        }
      } else if (response && response.data) {
        const list = response.data?.data || response.data || [];
        setCustomers(Array.isArray(list) ? list : []);
        if (response.data?.stats) {
          setStats(response.data.stats);
        }
      } else if (Array.isArray(response)) {
        setCustomers(response);
      }
    } catch (err) {
      console.error('Error cargando clientes:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch Catalogs (Vendors & Zones)
  const fetchCatalogs = async () => {
    try {
      let vendorList = [];
      try {
        if (UserApiManager && typeof UserApiManager.getSellers === 'function') {
          const resVendors = await UserApiManager.getSellers();
          vendorList = Array.isArray(resVendors?.data)
            ? resVendors.data
            : (Array.isArray(resVendors?.data?.data) ? resVendors.data.data : (Array.isArray(resVendors) ? resVendors : []));
        }
      } catch (sellerErr) {
        console.warn('Error fetching sellers via getSellers, intentando fallback con getUsers:', sellerErr);
      }

      if (!vendorList || vendorList.length === 0) {
        if (UserApiManager && typeof UserApiManager.getUsers === 'function') {
          const resVendors = await UserApiManager.getUsers({ rol: 'Vendedor', per_page: 2500 });
          vendorList = Array.isArray(resVendors?.data?.data)
            ? resVendors.data.data
            : (Array.isArray(resVendors?.data) ? resVendors.data : (Array.isArray(resVendors) ? resVendors : []));
        } else {
          const resVendors = await axiosInstance.get('/admin/sellers').catch(() =>
            axiosInstance.get('/admin/users', { params: { rol: 'Vendedor', per_page: 2500 } })
          );
          vendorList = Array.isArray(resVendors?.data?.data)
            ? resVendors.data.data
            : (Array.isArray(resVendors?.data) ? resVendors.data : []);
        }
      }
      setVendors(Array.isArray(vendorList) ? vendorList : []);

      const resZones = await axiosInstance.get('/admin/zones');
      setZones(resZones.data?.data || []);

      try {
        const resSettings = await axiosInstance.get('/core/settings');
        const settingCanales = resSettings?.data?.data?.credit_settings?.canales;
        if (settingCanales) {
          setChannelConfig(prev => {
            const next = { ...prev };
            Object.keys(settingCanales).forEach(k => {
              if (next[k]) {
                next[k] = {
                  ...next[k],
                  limite: settingCanales[k].limite_credito !== undefined ? parseFloat(settingCanales[k].limite_credito) : next[k].limite,
                  dias_credito: settingCanales[k].dias_credito !== undefined ? parseInt(settingCanales[k].dias_credito, 10) : next[k].dias_credito
                };
              }
            });
            return next;
          });
        }
      } catch (errSettings) {
        // silent fallback to default CANAL_CONFIG
      }
    } catch (err) {
      console.error('Error al cargar catálogos:', err);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, [search, statusFilter, zoneFilter, rubroFilter, canalFilter]);

  // Sincronizar zoneFilter cuando cambie la URL (?zone_id=unassigned u otra)
  useEffect(() => {
    const paramZone = searchParams.get('zone_id');
    if (paramZone !== null && paramZone !== zoneFilter) {
      setZoneFilter(paramZone);
    }
  }, [searchParams]);

  const handleZoneFilterChange = (val) => {
    setZoneFilter(val);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (val) {
        next.set('zone_id', val);
      } else {
        next.delete('zone_id');
      }
      return next;
    }, { replace: true });
  };

  useEffect(() => {
    fetchCatalogs();
  }, []);

  // Download official excel template
  const handleDownloadTemplate = async () => {
    try {
      if (CustomerApiManager && typeof CustomerApiManager.downloadImportTemplate === 'function') {
        const responseData = await CustomerApiManager.downloadImportTemplate();
        const blob = responseData instanceof Blob
          ? responseData
          : new Blob([responseData], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'plantilla_carga_masiva_clientes.xlsx');
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } else {
        alert('Servicio de descarga de plantilla no disponible.');
      }
    } catch (err) {
      console.error('Error al descargar plantilla:', err);
      alert('No se pudo descargar la plantilla oficial.');
    }
  };

  // Drag & Drop handlers for bulk import
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      if (file.name.endsWith('.xlsx') || file.name.endsWith('.csv') || file.name.endsWith('.xls')) {
        setImportFile(file);
      } else {
        alert('Por favor selecciona un archivo con formato Excel (.xlsx) o CSV (.csv).');
      }
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files);
      setDocumentFiles(prev => {
        const existingKeys = new Set(prev.map(f => `${f.name}-${f.size}`));
        const uniqueNewFiles = newFiles.filter(f => !existingKeys.has(`${f.name}-${f.size}`));
        return [...prev, ...uniqueNewFiles];
      });
    }
    e.target.value = '';
  };

  const getDocumentUrl = (filePath) => {
    return filePath || '#';
  };

  // Filter customers by active main tab
  const filteredCustomers = customers.filter(c => {
    const s = c.estatus?.toLowerCase() || '';
    switch(activeMainTab) {
      case CUSTOMER_STATUS.PENDING_APPROVAL:
        return s === CUSTOMER_STATUS.PENDING_APPROVAL || s === 'pendiente';
      case CUSTOMER_STATUS.REQUIRES_VISIT:
        return s === CUSTOMER_STATUS.REQUIRES_VISIT;
      case CUSTOMER_STATUS.ACTIVE:
        return s === CUSTOMER_STATUS.ACTIVE || s === 'activo';
      case CUSTOMER_STATUS.DISABLED:
        return s === CUSTOMER_STATUS.DISABLED || s === 'bloqueado' || s === CUSTOMER_STATUS.REJECTED || s === 'rechazado';
      default:
        return true;
    }
  });

  // Inspect Modal handlers
  const handleInspect = async (customer) => {
    try {
      setLoading(true);
      const res = await axiosInstance.get(`/admin/customers/audit-requests/${customer.id}`);
      const targetCustomer = (res.data && res.data.success) ? res.data.data : customer;
      setInspectCustomer(targetCustomer);

      // Fetch delivery addresses & GPS coordinates (HU38)
      try {
        const addrRes = await axiosInstance.get(`/customers/${customer.id}/addresses`);
        if (addrRes.data && addrRes.data.success && Array.isArray(addrRes.data.data)) {
          setCustomerAddresses(addrRes.data.data);
        } else {
          setCustomerAddresses([]);
        }
      } catch (e) {
        setCustomerAddresses([]);
      }
    } catch (err) {
      setInspectCustomer(customer);
      setCustomerAddresses([]);
    } finally {
      setLoading(false);
      setSelectedAddressIndex(0);
      setShowInspectModal(true);
    }
  };

  const getLatestCompletedFieldTask = (customer) => {
    if (!customer || !Array.isArray(customer.tasks) || customer.tasks.length === 0) return null;
    return customer.tasks.find(tk => ['completado', 'completada'].includes(tk.estado?.toLowerCase()) && tk.resultado) || null;
  };

  const handleOpenVisitModal = (customer) => {
    setInspectCustomer(customer);
    setVisitData({
      notas: ''
    });
    setShowVisitModal(true);
  };

  const handleOpenApproveModal = (customer) => {
    setInspectCustomer(customer);
    const canalCfg = getCanalCfg(customer.canal_comercial);
    const suggestedLimit = customer.limite_credito > 0 
      ? customer.limite_credito 
      : (canalCfg?.limite || 0);
    const suggestedDays = customer.dias_gracia > 0
      ? customer.dias_gracia
      : (canalCfg?.dias_credito || 8);
    const resolvedZoneId = customer.zone_id || customer.zone?.id || '';
    const zoneObj = customer.zone || zones.find(z => z.id === resolvedZoneId);
    const resolvedVendorId = customer.vendedor_id || customer.vendedor?.id || zoneObj?.vendedor_id || '';
    setApproveData({
      limite_credito: suggestedLimit,
      dias_gracia: suggestedDays,
      vendedor_id: resolvedVendorId,
      zone_id: resolvedZoneId
    });
    setShowApproveModal(true);
  };

  const handleOpenRejectModal = (customer) => {
    setInspectCustomer(customer);
    const completedTask = getLatestCompletedFieldTask(customer);
    let suggestedMotivo = '';
    if (completedTask && completedTask.resultado === 'rejected') {
      const reasons = Array.isArray(completedTask.motivos_rechazo) ? completedTask.motivos_rechazo.filter(Boolean) : [];
      const parts = [];
      if (reasons.length > 0) {
        parts.push(`Inspección de campo: ${reasons.join('; ')}.`);
      }
      if (completedTask.observaciones_vendedor) {
        parts.push(`Observación: ${completedTask.observaciones_vendedor}`);
      }
      suggestedMotivo = parts.join(' ').slice(0, 500);
    }
    setRejectData({ motivo: suggestedMotivo });
    setShowRejectModal(true);
  };

  const handleConfirmVisitRequest = async (e) => {
    e.preventDefault();
    if (!inspectCustomer) return;
    setProcessing(true);

    try {
      const res = await axiosInstance.post(`/admin/customers/${inspectCustomer.id}/request-visit`, {
        notas: visitData.notas || ''
      });
      setSuccessNotification(res.data?.message || 'Visita de campo solicitada y notificada al vendedor titular de la zona.');
      setShowVisitModal(false);
      setShowInspectModal(false);
      fetchCustomers();
    } catch (err) {
      alert(err.response?.data?.message || 'Error al solicitar visita comercial.');
    } finally {
      setProcessing(false);
    }
  };

  const handleConfirmApprove = async (e) => {
    e.preventDefault();
    if (!inspectCustomer) return;
    setProcessing(true);

    try {
      const res = await axiosInstance.patch(`/admin/customers/${inspectCustomer.id}/audit-approve`, approveData);
      alert(res.data?.message || 'Cliente aprobado exitosamente. Cuenta activa para operar.');
      setShowApproveModal(false);
      setShowInspectModal(false);
      fetchCustomers();
    } catch (err) {
      alert(err.response?.data?.message || 'Error al aprobar cliente.');
    } finally {
      setProcessing(false);
    }
  };

  const handleConfirmReject = async (e) => {
    e.preventDefault();
    if (!inspectCustomer) return;
    setProcessing(true);

    try {
      const res = await axiosInstance.patch(`/admin/customers/${inspectCustomer.id}/audit-reject`, rejectData);
      alert(res.data?.message || 'Solicitud rechazada.');
      setShowRejectModal(false);
      setShowInspectModal(false);
      fetchCustomers();
    } catch (err) {
      alert(err.response?.data?.message || 'Error al rechazar solicitud.');
    } finally {
      setProcessing(false);
    }
  };

  // Open Edit / Create Manual Modal
  const handleOpenEditModal = (customer = null) => {
    setValidationError('');
    setInvalidFields({});
    setDocumentFiles([]);
    if (customer) {
      setEditingCustomer(customer);
      setEditingId(customer.id);
      setExistingDocuments(customer.documents || []);
      let lat = '';
      let lng = '';
      if (customer.ubicacion_gps) {
        const parts = customer.ubicacion_gps.split(',');
        if (parts.length === 2) {
          lat = parts[0].trim();
          lng = parts[1].trim();
        }
      }

      setFormData({
        rif_cedula: customer.rif_cedula || '',
        razon_social: customer.razon_social || '',
        nombre_comercial: customer.nombre_comercial || '',
        nombre_contacto: customer.nombre_contacto || '',
        direccion_fisica: customer.direccion_fisica || '',
        latitude: lat,
        longitude: lng,
        ubicacion_gps: customer.ubicacion_gps || '',
        email_facturacion: customer.email_facturacion || '',
        telefono: customer.telefono || '',
        canal_comercial: customer.canal_comercial || '',
        subcategoria_comercial: customer.subcategoria_comercial || customer.rubro || '',
        categoria: customer.categoria || '',
        segmentacion: customer.segmentacion || '',
        limite_credito: customer.limite_credito || 0,
        dias_gracia: customer.dias_gracia !== undefined && customer.dias_gracia !== null ? customer.dias_gracia : 8,
        zone_id: customer.zone_id || '',
        vendedor_id: customer.vendedor_id || ''
      });
    } else {
      setEditingCustomer(null);
      setEditingId(null);
      setExistingDocuments([]);
      setFormData(initialForm);
    }
    setShowModal(true);
  };

  // Helper to calculate official SENIAT Modulo 11 check digit
  const calculateSeniatCheckDigit = (type, number) => {
    const typeMap = { V: 1, E: 2, J: 3, P: 4, G: 5 };
    const weights = [3, 2, 7, 6, 5, 4, 3, 2];
    const tVal = typeMap[type] || 1;
    const numStr = String(number).replace(/\D/g, '').slice(0, 8).padStart(8, '0');
    let sum = tVal * 4;
    for (let i = 0; i < 8; i++) {
      sum += parseInt(numStr[i], 10) * weights[i];
    }
    const rem = sum % 11;
    let check = 11 - rem;
    if (check >= 10) check = 0;
    return check;
  };

  // Helper to format/standardize RIF input strictly (J-xxxxxxxx-x for companies, V-xxxxxxxx for natural cédula)
  const formatRif = (val) => {
    if (!val) return '';
    let clean = val.toUpperCase().replace(/[\s-]/g, '');
    if (/^\d+$/.test(clean)) {
      clean = 'V' + clean;
    }
    const prefix = clean.charAt(0);
    const digits = clean.slice(1).replace(/\D/g, '');

    // Case 1: Corporate / Government (J, G) -> strictly J-xxxxxxxx-x
    if (['J', 'G'].includes(prefix)) {
      if (digits.length === 9) {
        return `${prefix}-${digits.slice(0, 8)}-${digits.slice(8, 9)}`;
      }
      if (digits.length >= 6 && digits.length <= 8) {
        const base = digits.padStart(8, '0');
        const cd = calculateSeniatCheckDigit(prefix, base);
        return `${prefix}-${base}-${cd}`;
      }
      if (digits.length >= 10) {
        return `${prefix}-${digits.slice(0, 8)}-${digits.slice(8, 9)}`;
      }
    }

    // Case 2: Explicit 2 hyphens for V/E with check digit
    if (['V', 'E', 'P'].includes(prefix) && (val.match(/-/g) || []).length >= 2) {
      if (digits.length === 9) {
        return `${prefix}-${digits.slice(0, 8)}-${digits.slice(8, 9)}`;
      }
    }

    // Case 3: Natural / Foreign cédula (V, E) -> strictly V-xxxxxxxx
    if (['V', 'E', 'P'].includes(prefix)) {
      if (digits.length === 9) {
        return `${prefix}-${digits.slice(0, 8)}`;
      }
      if (digits.length >= 6 && digits.length <= 8) {
        return `${prefix}-${digits}`;
      }
    }

    return val.trim().toUpperCase();
  };

  const handleRifBlur = () => {
    if (!formData.rif_cedula) return;
    const formatted = formatRif(formData.rif_cedula);
    if (formatted !== formData.rif_cedula) {
      setFormData(prev => ({ ...prev, rif_cedula: formatted }));
    }
  };

  // Determine if the customer being edited is already validated / active
  const isEditingValidatedCustomer = Boolean(
    editingCustomer && (
      editingCustomer.estatus === CUSTOMER_STATUS.ACTIVE ||
      editingCustomer.estatus === 'activo' ||
      editingCustomer.estatus === CUSTOMER_STATUS.DISABLED ||
      editingCustomer.estatus === 'bloqueado' ||
      editingCustomer.estatus === 'deshabilitado' ||
      editingCustomer.approved_at
    )
  );

  // Submit Create/Edit/Import
  const handleSaveCustomer = async (e) => {
    e.preventDefault();
    setValidationError('');
    setInvalidFields({});

    // Auto-normalize RIF before validation
    let normalizedRif = formatRif(formData.rif_cedula);
    if (normalizedRif && normalizedRif !== formData.rif_cedula) {
      setFormData(prev => ({ ...prev, rif_cedula: normalizedRif }));
    }

    // Client side validation
    if (modalTab === 'manual' || editingCustomer || editingId) {
      const errors = {};
      if (!normalizedRif) {
        errors.rif_cedula = true;
      } else if (!/^([JG]-\d{7,8}-\d|[VEP]-\d{6,8}(-\d)?|[JVRGEPC]-\d{6,8}(-\d)?-\d+)$/i.test(normalizedRif)) {
        errors.rif_cedula = 'Formato inválido. Use J-12345678-9 para empresas o V-12345678 para personas naturales.';
      }
      if (!formData.razon_social || !formData.razon_social.trim()) errors.razon_social = true;
      if (!formData.telefono || !formData.telefono.trim()) errors.telefono = true;
      if (!formData.direccion_fisica || !formData.direccion_fisica.trim()) errors.direccion_fisica = true;

      if (Object.keys(errors).length > 0) {
        setInvalidFields(errors);
        setValidationError(typeof errors.rif_cedula === 'string' ? errors.rif_cedula : 'Todos los campos marcados son obligatorios.');
        return;
      }
    }

    setProcessing(true);
    try {
      const targetId = editingCustomer?.id || editingId;
      if (targetId) {
        const updatePayload = {
          ...formData,
          rif_cedula: normalizedRif,
          limite_credito: parseFloat(formData.limite_credito || 0),
          dias_gracia: parseInt(formData.dias_gracia || 8, 10)
        };
        if (formData.latitude && formData.longitude) {
          updatePayload.latitude = parseFloat(formData.latitude);
          updatePayload.longitude = parseFloat(formData.longitude);
          updatePayload.ubicacion_gps = `${formData.latitude},${formData.longitude}`;
        } else if (formData.ubicacion_gps) {
          const parts = String(formData.ubicacion_gps).trim().split(/[,\s;]+/);
          if (parts.length >= 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
            updatePayload.latitude = parseFloat(parts[0]);
            updatePayload.longitude = parseFloat(parts[1]);
            updatePayload.ubicacion_gps = `${parts[0]},${parts[1]}`;
          }
        }
        if (CustomerApiManager && typeof CustomerApiManager.updateCustomer === 'function') {
          await CustomerApiManager.updateCustomer(targetId, updatePayload);
        } else {
          await axiosInstance.put(`/admin/customers/${targetId}`, updatePayload);
        }
        setSuccessNotification(t('customer_updated_success', 'Cliente actualizado exitosamente.'));
        setShowModal(false);
        fetchCustomers();
      } else {
        if (modalTab === 'import') {
          if (!importFile) {
            setValidationError('Por favor selecciona un archivo Excel (.xlsx) o CSV (.csv).');
            setProcessing(false);
            return;
          }
          const form = new FormData();
          form.append('file', importFile);
          
          try {
            let response;
            if (CustomerApiManager && typeof CustomerApiManager.importCustomers === 'function') {
              response = await CustomerApiManager.importCustomers(form);
            } else {
              response = await axiosInstance.post('/admin/customers/import', form, {
                headers: { 'Content-Type': 'multipart/form-data' }
              });
            }
            setShowModal(false);
            setImportFile(null);
            setSuccessNotification(response?.data?.message || 'Clientes importados y pre-activados exitosamente.');
            fetchCustomers();
          } catch (err) {
            console.error('Error en carga masiva:', err.response?.data);
            if (err.response?.status === 422) {
              const resData = err.response.data;
              if (Array.isArray(resData?.errors) && resData.errors.length > 0) {
                setImportErrors(resData.errors);
                setShowImportErrorModal(true);
                setShowModal(false);
              } else if (resData?.errors && typeof resData.errors === 'object') {
                const errorArray = [];
                Object.keys(resData.errors).forEach(k => {
                  const msgs = Array.isArray(resData.errors[k]) ? resData.errors[k] : [resData.errors[k]];
                  msgs.forEach(m => errorArray.push({ row: '-', field: k, reason: m }));
                });
                setImportErrors(errorArray);
                setShowImportErrorModal(true);
                setShowModal(false);
              } else {
                setValidationError(resData?.message || 'Error de validación al procesar el archivo.');
              }
            } else {
              setValidationError(err.response?.data?.message || 'Error al procesar la importación masiva.');
            }
          }
        } else {
          // Manual Creation
          let payload;
          if (documentFiles.length > 0) {
            payload = new FormData();
            payload.append('rif_cedula', formData.rif_cedula);
            payload.append('razon_social', formData.razon_social);
            if (formData.nombre_comercial) payload.append('nombre_comercial', formData.nombre_comercial);
            if (formData.nombre_contacto) payload.append('nombre_contacto', formData.nombre_contacto);
            payload.append('direccion_fisica', formData.direccion_fisica);
            if (formData.email_facturacion) payload.append('email_facturacion', formData.email_facturacion);
            payload.append('telefono', formData.telefono);
            if (formData.canal_comercial) payload.append('canal_comercial', formData.canal_comercial);
            if (formData.subcategoria_comercial) payload.append('subcategoria_comercial', formData.subcategoria_comercial);
            payload.append('categoria', formData.categoria || formData.subcategoria_comercial || 'General');
            if (formData.segmentacion) payload.append('segmentacion', formData.segmentacion);
            payload.append('limite_credito', parseFloat(formData.limite_credito || 0));
            payload.append('dias_gracia', parseInt(formData.dias_gracia || 8, 10));
            if (formData.vendedor_id) payload.append('vendedor_id', formData.vendedor_id);
            if (formData.zone_id) payload.append('zone_id', formData.zone_id);
            if (formData.latitude) payload.append('latitude', formData.latitude);
            if (formData.longitude) payload.append('longitude', formData.longitude);
            if (formData.ubicacion_gps) payload.append('ubicacion_gps', formData.ubicacion_gps);
            documentFiles.forEach(file => {
              payload.append('documents[]', file);
            });
          } else {
            payload = {
              ...formData,
              limite_credito: parseFloat(formData.limite_credito || 0),
              dias_gracia: parseInt(formData.dias_gracia || 8, 10)
            };
            if (formData.latitude && formData.longitude) {
              payload.latitude = parseFloat(formData.latitude);
              payload.longitude = parseFloat(formData.longitude);
              payload.ubicacion_gps = `${formData.latitude},${formData.longitude}`;
            }
          }

          if (CustomerApiManager && typeof CustomerApiManager.createCustomer === 'function') {
            await CustomerApiManager.createCustomer(payload);
          } else {
            await axiosInstance.post('/admin/customers', payload);
          }

          setSuccessNotification(t('customer_created_success', 'Cliente registrado exitosamente.'));
          setShowModal(false);
          setDocumentFiles([]);
          fetchCustomers();
        }
      }
    } catch (err) {
      console.error('Error en operación:', err);
      if (err.response?.status === 422 && err.response?.data?.errors) {
        const serverErrors = err.response.data.errors;
        const fieldErrors = {};
        Object.keys(serverErrors).forEach(key => {
          const msg = Array.isArray(serverErrors[key]) ? serverErrors[key][0] : serverErrors[key];
          fieldErrors[key] = msg;
          if (key === 'rif') fieldErrors.rif_cedula = msg;
          if (key === 'name') fieldErrors.razon_social = msg;
          if (key === 'direccion' || key === 'address') fieldErrors.direccion_fisica = msg;
          if (key === 'email') fieldErrors.email_facturacion = msg;
          if (key === 'phone') fieldErrors.telefono = msg;
          if (key === 'category') fieldErrors.categoria = msg;
        });

        setInvalidFields(fieldErrors);
        const errorMsg = Object.values(serverErrors).flat().join('. ');
        setValidationError(errorMsg || 'La información proporcionada no es válida.');
      } else {
        setValidationError(err.response?.data?.message || 'Error al procesar la solicitud del cliente.');
      }
    } finally {
      setProcessing(false);
    }
  };

  const handleToggleStatus = async (customer, newStatus) => {
    if (!window.confirm(`¿Seguro que deseas cambiar el estatus de este cliente a ${newStatus}?`)) return;
    
    try {
      await axiosInstance.patch(`/admin/customers/${customer.id}/toggle-status`, { estatus: newStatus });
      setSuccessNotification(`Estatus de cliente actualizado a ${newStatus}.`);
      fetchCustomers();
    } catch (err) {
      alert(err.response?.data?.message || 'Error actualizando estado');
    }
  };

  const getStatusBadge = (status, isResubmitted = false, tasks = []) => {
    const s = status?.toLowerCase();
    const completedTask = Array.isArray(tasks)
      ? tasks.find(tk => ['completado', 'completada'].includes(tk.estado?.toLowerCase()) && tk.resultado)
      : null;

    switch(s) {
      case CUSTOMER_STATUS.PENDING_APPROVAL:
      case 'pendiente':
        return (
          <div className="d-inline-flex flex-column align-items-start gap-1">
            <Badge bg="warning" text="dark" className="px-3 py-2 fw-bold">{t('status_pending_approval', 'Pendiente por Auditar')}</Badge>
            {completedTask?.resultado === 'approved' && (
              <Badge className="px-2 py-1 fw-bold shadow-sm" style={{ backgroundColor: '#16a34a', color: '#ffffff', fontSize: '0.73rem' }}>
                <i className="mdi mdi-map-marker-check me-1"></i>{t('badge_visit_approved', 'Visita Validada')}
              </Badge>
            )}
            {completedTask?.resultado === 'rejected' && (
              <Badge className="px-2 py-1 fw-bold shadow-sm" style={{ backgroundColor: '#dc2626', color: '#ffffff', fontSize: '0.73rem' }}>
                <i className="mdi mdi-map-marker-remove me-1"></i>{t('badge_visit_rejected', 'Visita Observada')}
              </Badge>
            )}
            {isResubmitted && (
              <Badge bg="info" className="px-2 py-1 fw-bold shadow-sm" style={{ backgroundColor: '#0284c7', color: '#ffffff', fontSize: '0.75rem' }}>
                <i className="mdi mdi-refresh me-1"></i>{t('datos_reenviados', 'Datos Reenviados')}
              </Badge>
            )}
          </div>
        );
      case CUSTOMER_STATUS.REQUIRES_VISIT:
        return <Badge style={{ backgroundColor: '#fd7e14', color: '#ffffff' }} className="px-3 py-2 fw-bold">{t('status_requires_visit', 'Requiere Visita de Campo')}</Badge>;
      case CUSTOMER_STATUS.ACTIVE:
      case 'activo':
        return <Badge bg="success" className="px-3 py-2 fw-bold">{t('status_active', 'Activo / Operativo')}</Badge>;
      case CUSTOMER_STATUS.DISABLED:
      case 'bloqueado':
        return <Badge bg="danger" className="px-3 py-2 fw-bold">{t('status_disabled', 'Deshabilitado / Bloqueado')}</Badge>;
      case CUSTOMER_STATUS.REJECTED:
      case 'rechazado':
        return <Badge bg="secondary" className="px-3 py-2 fw-bold">{t('status_rejected', 'Rechazado / No Aprobado')}</Badge>;
      case CUSTOMER_STATUS.INACTIVE:
      case 'inactivo':
        return <Badge bg="info" className="px-3 py-2 fw-bold">{t('status_inactive', 'Invitado / Sin Clave')}</Badge>;
      default:
        return <Badge bg="secondary" className="px-3 py-2">{status}</Badge>;
    }
  };

  const columns = [
    {
      name: t('th_rif', 'RIF / CÉDULA'),
      selector: row => row.rif_cedula,
      sortable: true,
      width: '135px',
      cell: row => (
        <span className="fw-bold text-dark" style={{ fontSize: '0.85rem' }}>
          {row.rif_cedula}
        </span>
      )
    },
    {
      name: t('th_razon_social', 'RAZÓN SOCIAL'),
      selector: row => row.razon_social,
      sortable: true,
      grow: 2,
      width: '220px',
      cell: row => (
        <div className="py-1">
          <div className="fw-bold text-dark" style={{ fontSize: '0.88rem', lineHeight: '1.2' }}>{row.razon_social}</div>
          {row.nombre_comercial && row.nombre_comercial !== row.razon_social && (
            <div className="text-primary small fw-semibold text-truncate" style={{ maxWidth: '220px', fontSize: '0.78rem' }}>
              <i className="mdi mdi-store-outline me-1"></i>{row.nombre_comercial}
            </div>
          )}
          <small className="text-muted d-block text-truncate mt-1" style={{ maxWidth: '220px', fontSize: '0.75rem' }}>
            {row.nombre_contacto ? `${row.nombre_contacto} • ` : ''}{row.email_facturacion || row.telefono || 'Sin contacto'}
          </small>
        </div>
      )
    },
    {
      name: t('th_rubro_frequency', 'CANAL / SUB-CATEGORÍA'),
      selector: row => row.canal_comercial || row.rubro || row.categoria || 'N/A',
      sortable: true,
      width: '200px',
      cell: row => {
        const canal = row.canal_comercial;
        const config = CANAL_CONFIG[canal];
        const subcatText = row.subcategoria_comercial || row.rubro || row.categoria;
        const isSegmented = !row.requires_segmentation && (canal || subcatText);
        const localizedSubcat = getSubcatLabel(canal, row.subcategoria_comercial, t) || (subcatText ? subcatText.replace(/_/g, ' ') : '');

        return (
          <div className="py-1">
            {config ? (
              <Badge 
                className="px-2 py-1 fw-bold text-uppercase d-inline-block text-truncate mb-1" 
                style={{ 
                  backgroundColor: config.color, 
                  color: '#ffffff', 
                  fontSize: '0.68rem',
                  maxWidth: '175px'
                }}
              >
                {getCanalBadgeLabel(canal, t)}
              </Badge>
            ) : (
              <Badge 
                className="px-2 py-1 fw-bold text-uppercase d-inline-block text-truncate mb-1" 
                style={{ 
                  backgroundColor: isSegmented ? '#00024f' : '#6c757d', 
                  color: '#ffffff', 
                  fontSize: '0.68rem',
                  maxWidth: '175px'
                }}
              >
                {isSegmented ? (localizedSubcat || t('segmented_done', 'Segmentado')) : t('unsegmented', 'Sin segmentar')}
              </Badge>
            )}

            {localizedSubcat && config && (
              <div className="fw-semibold text-capitalize" style={{ fontSize: '0.75rem', color: isDark ? '#cbd5e1' : '#010028' }}>
                {localizedSubcat}
              </div>
            )}
          </div>
        );
      }
    },
    {
      name: t('th_assigned_vendor', 'VENDEDOR / ZONA'),
      selector: row => row.vendedor?.nombre_completo || row.zone?.name || 'N/A',
      sortable: true,
      width: '210px',
      cell: row => (
        <div className="py-1" style={{ maxWidth: '195px' }}>
          {row.vendedor ? (
            <div className="fw-semibold text-truncate" style={{ fontSize: '0.82rem' }}>
              <i className="mdi mdi-account-tie me-1 text-primary"></i>
              {row.vendedor.nombre_completo}
            </div>
          ) : (
            <span className="text-muted small d-block" style={{ fontSize: '0.78rem' }}>{t('unassigned', 'Sin Vendedor')}</span>
          )}
          {row.zone ? (
            <Badge bg="primary" className="fw-normal d-inline-block mt-1 text-truncate" style={{ fontSize: '0.7rem', maxWidth: '190px' }}>
              {row.zone.name} {row.zone.code ? `(${row.zone.code})` : ''}
            </Badge>
          ) : (
            <span className="badge rounded-pill mt-1 d-inline-flex align-items-center gap-1" style={{ backgroundColor: 'rgba(239, 68, 68, 0.12)', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.3)', fontSize: '0.7rem', padding: '3px 7px' }}>
              <i className="mdi mdi-map-marker-off-outline"></i>
              {t('unassigned_zone_short', 'Sin Zona')}
            </span>
          )}
        </div>
      )
    },
    {
      name: t('th_credito_plazo', 'LÍMITE / PLAZO'),
      selector: row => parseFloat(row.limite_credito || 0),
      sortable: true,
      width: '160px',
      cell: row => {
        const dias = row.dias_gracia !== undefined && row.dias_gracia !== null ? row.dias_gracia : 8;
        return (
          <div className="w-100">
            <span className="fw-bold d-block" style={{ fontSize: '0.88rem', color: '#010028' }}>
              ${parseFloat(row.limite_credito || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
            <span className="badge rounded-pill bg-light text-dark border d-inline-block mt-1" style={{ fontSize: '0.72rem', fontWeight: 500 }}>
              {dias} {t('credit_days_badge', 'días')}
            </span>
          </div>
        );
      }
    },
    {
      name: t('th_status', 'ESTADO'),
      selector: row => row.estatus,
      sortable: true,
      width: '185px',
      cell: row => getStatusBadge(row.estatus, row.is_resubmitted, row.tasks)
    },
    {
      name: t('th_actions', 'ACCIONES'),
      width: '150px',
      cell: row => {
        const estatusLower = row.estatus?.toLowerCase();

        // 1. Pending Audit
        if (['pending_approval', 'pendiente'].includes(estatusLower)) {
          return (
            <Button 
              variant="warning" 
              size="sm" 
              className="fw-bold text-dark w-100 shadow-sm d-flex align-items-center justify-content-center" 
              style={{ borderRadius: '6px', fontSize: '0.78rem', padding: '5px 8px' }} 
              onClick={() => handleInspect(row)}
            >
              <i className="mdi mdi-file-search me-1"></i> Auditar
            </Button>
          );
        }

        // 2. Requires Field Visit
        if (estatusLower === 'requires_visit') {
          return (
            <Button 
              style={{ backgroundColor: '#fd7e14', borderColor: '#fd7e14', color: 'white', borderRadius: '6px', fontSize: '0.78rem', padding: '5px 8px' }} 
              size="sm" 
              className="fw-bold w-100 shadow-sm d-flex align-items-center justify-content-center" 
              onClick={() => handleInspect(row)}
            >
              <i className="mdi mdi-truck-delivery me-1"></i> Visita
            </Button>
          );
        }

        // 3. Normal / Active / Disabled / Rejected
        return (
          <div className="d-flex align-items-center gap-1">
            <button 
              className="btn btn-sm btn-outline-info rounded-circle d-flex align-items-center justify-content-center" 
              style={{ width: '32px', height: '32px', padding: 0 }} 
              onClick={() => handleInspect(row)}
              title={t('btn_view_details', 'Ver Detalles, Documentos y Ubicación')}
            >
              <i className="mdi mdi-eye-outline" style={{ fontSize: '1.1rem' }}></i>
            </button>
            <button 
              className="btn btn-sm btn-outline-primary rounded-circle d-flex align-items-center justify-content-center" 
              style={{ width: '32px', height: '32px', padding: 0 }} 
              onClick={() => handleOpenEditModal(row)} 
              title={t('edit', 'Editar Datos')}
            >
              <i className="mdi mdi-pencil" style={{ fontSize: '1.05rem' }}></i>
            </button>
            {['active', 'activo'].includes(estatusLower) ? (
              <button 
                className="btn btn-sm btn-outline-danger rounded-circle d-flex align-items-center justify-content-center" 
                style={{ width: '32px', height: '32px', padding: 0 }} 
                onClick={() => handleToggleStatus(row, 'disabled')}
                title={t('btn_block', 'Bloquear Cliente')}
              >
                <i className="mdi mdi-lock" style={{ fontSize: '1.05rem' }}></i>
              </button>
            ) : (
              <button 
                className="btn btn-sm btn-outline-success rounded-circle d-flex align-items-center justify-content-center" 
                style={{ width: '32px', height: '32px', padding: 0 }} 
                onClick={() => handleToggleStatus(row, 'active')}
                title={t('btn_unblock', 'Activar Cliente')}
              >
                <i className="mdi mdi-lock-open" style={{ fontSize: '1.05rem' }}></i>
              </button>
            )}
          </div>
        );
      }
    }
  ];

  return (
    <>
      {/* PAGE HEADER */}
      <div className="page-header d-flex justify-content-between align-items-center mb-4">
        <div>
          <h3 className="page-title" style={{ color: '#010028', fontWeight: 'bold' }}> {t('customers_title', 'Gestión de Clientes')} </h3>
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb mb-0">
              <li className="breadcrumb-item"><a href="!#" onClick={e => e.preventDefault()}>{t('dashboard', 'Panel')}</a></li>
              <li className="breadcrumb-item active" aria-current="page">{t('customers', 'Clientes')}</li>
            </ol>
          </nav>
        </div>
        <div>
          <Button 
            className="shadow-sm"
            style={{ backgroundColor: '#183eeb', borderColor: '#183eeb', color: 'white', borderRadius: '10px' }} 
            onClick={() => handleOpenEditModal(null)}
          >
            <i className="mdi mdi-account-plus me-1"></i> {t('btn_create_customer', 'Crear Clientes')}
          </Button>
        </div>
      </div>

      {/* SUCCESS NOTIFICATION ALERT */}
      {successNotification && (
        <div 
          className="mb-4 d-flex align-items-center justify-content-between p-3 shadow-sm"
          style={{ 
            borderRadius: '10px', 
            backgroundColor: isDark ? 'rgba(16, 185, 129, 0.12)' : '#ecfdf5', 
            border: isDark ? '1px solid rgba(16, 185, 129, 0.35)' : '1px solid #a7f3d0',
            borderLeft: '4px solid #10b981',
            color: isDark ? '#a7f3d0' : '#064e3b'
          }}
          role="alert"
        >
          <div className="d-flex align-items-center gap-2">
            <i className="mdi mdi-check-circle" style={{ fontSize: '1.25rem', color: isDark ? '#34d399' : '#10b981' }}></i>
            <span className="fw-semibold" style={{ fontSize: '0.88rem' }}>{successNotification}</span>
          </div>
          <button 
            type="button" 
            onClick={() => setSuccessNotification('')} 
            className="btn btn-sm p-0 d-flex align-items-center justify-content-center"
            style={{ 
              width: '26px', 
              height: '26px', 
              borderRadius: '50%',
              color: isDark ? '#a7f3d0' : '#064e3b',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              opacity: 0.75,
              transition: 'opacity 0.2s ease, background-color 0.2s ease'
            }}
            onMouseEnter={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.backgroundColor = isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)'; }}
            onMouseLeave={e => { e.currentTarget.style.opacity = '0.75'; e.currentTarget.style.backgroundColor = 'transparent'; }}
            aria-label="Cerrar notificación"
          >
            <i className="mdi mdi-close" style={{ fontSize: '1.15rem' }}></i>
          </button>
        </div>
      )}

      {/* KPI STAT CARDS INTERACTIVAS */}
      <Row className="mb-4 g-3">
        <Col md={3}>
          <Card 
            className="shadow-sm border-0 text-white" 
            style={{ 
              backgroundColor: '#00024f', 
              borderRadius: '12px', 
              cursor: 'pointer',
              outline: statusFilter === '' ? '3px solid #183eeb' : 'none',
              transform: statusFilter === '' ? 'scale(1.02)' : 'none',
              transition: 'all 0.2s ease'
            }}
            onClick={() => setStatusFilter('')}
            title={t('status_all', 'Ver todos los clientes')}
          >
            <Card.Body className="d-flex align-items-center justify-content-between p-3">
              <div>
                <div className="text-white-50 small font-weight-bold text-uppercase">{t('total_customers_kpi', 'Total Clientes')}</div>
                <div className="h3 font-weight-bold mb-0">{stats.total}</div>
              </div>
              <i className="mdi mdi-domain" style={{ fontSize: '2.3rem', opacity: 0.8 }}></i>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card 
            className="shadow-sm border-0 text-white" 
            style={{ 
              backgroundColor: '#ffc107', 
              borderRadius: '12px', 
              cursor: 'pointer',
              outline: statusFilter === 'pending_approval' ? '3px solid #00024f' : 'none',
              transform: statusFilter === 'pending_approval' ? 'scale(1.02)' : 'none',
              transition: 'all 0.2s ease'
            }}
            onClick={() => setStatusFilter('pending_approval')}
            title={t('status_pending_approval', 'Pendiente por Auditar')}
          >
            <Card.Body className="d-flex align-items-center justify-content-between p-3">
              <div>
                <div className="small font-weight-bold text-uppercase text-dark">{t('status_pending_approval', 'Pendiente por Auditar')}</div>
                <div className="h3 font-weight-bold mb-0 text-dark">{stats.pending}</div>
              </div>
              <i className="mdi mdi-account-clock text-dark" style={{ fontSize: '2.3rem', opacity: 0.8 }}></i>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card 
            className="shadow-sm border-0 text-white" 
            style={{ 
              backgroundColor: '#fd7e14', 
              borderRadius: '12px', 
              cursor: 'pointer',
              outline: statusFilter === 'requires_visit' ? '3px solid #00024f' : 'none',
              transform: statusFilter === 'requires_visit' ? 'scale(1.02)' : 'none',
              transition: 'all 0.2s ease'
            }}
            onClick={() => setStatusFilter('requires_visit')}
            title={t('status_requires_visit', 'Requiere Visita de Campo')}
          >
            <Card.Body className="d-flex align-items-center justify-content-between p-3">
              <div>
                <div className="text-white-50 small font-weight-bold text-uppercase">{t('status_requires_visit', 'Requiere Visita Campo')}</div>
                <div className="h3 font-weight-bold mb-0">{stats.requiresVisit}</div>
              </div>
              <i className="mdi mdi-truck-delivery-outline" style={{ fontSize: '2.3rem', opacity: 0.8 }}></i>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card 
            className="shadow-sm border-0 text-white" 
            style={{ 
              backgroundColor: '#198754', 
              borderRadius: '12px', 
              cursor: 'pointer',
              outline: statusFilter === 'active' ? '3px solid #00024f' : 'none',
              transform: statusFilter === 'active' ? 'scale(1.02)' : 'none',
              transition: 'all 0.2s ease'
            }}
            onClick={() => setStatusFilter('active')}
            title={t('status_active', 'Activos / Operativos')}
          >
            <Card.Body className="d-flex align-items-center justify-content-between p-3">
              <div>
                <div className="text-white-50 small font-weight-bold text-uppercase">{t('status_active', 'Activos / Operativos')}</div>
                <div className="h3 font-weight-bold mb-0">{stats.active}</div>
              </div>
              <i className="mdi mdi-check-circle-outline" style={{ fontSize: '2.3rem', opacity: 0.8 }}></i>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* CONTENEDOR PRINCIPAL DE CLIENTES */}
      <div className="card shadow-sm border-0" style={{ borderRadius: '12px', backgroundColor: isDark ? '#161e38' : '#ffffff', border: isDark ? '1px solid #2a3759' : 'none' }}>
        <div className="card-body p-3">
          <Row className="mb-3 g-2 align-items-center">
            <Col md={3} className="mb-2 mb-md-0">
              <Form.Control 
                placeholder={t('search_customer_placeholder', 'Buscar por RIF, Razón Social o Email...')}
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="customer-filter-control w-100 shadow-none"
              />
            </Col>
            <Col md={3} className="mb-2 mb-md-0">
              <Form.Select 
                value={canalFilter} 
                onChange={e => setCanalFilter(e.target.value)}
                className="customer-filter-control w-100 cursor-pointer shadow-none"
              >
                <option value="">{t('all_canales', 'Todos los Canales')}</option>
                <option value="transformadores">{t('canal_transformadores', 'Transformadores (Food Service)')}</option>
                <option value="supermercados_bodegones">{t('canal_supermercados_bodegones', 'Supermercados y Bodegones (Retail)')}</option>
                <option value="tiendas_bodegas">{t('canal_tiendas_bodegas', 'Tiendas y Bodegas (Tradicional)')}</option>
              </Form.Select>
            </Col>
            <Col md={2} className="mb-2 mb-md-0">
              <Form.Select 
                value={rubroFilter} 
                onChange={e => setRubroFilter(e.target.value)}
                className="customer-filter-control w-100 cursor-pointer shadow-none"
              >
                <option value="">{t('all_rubros', 'Todos los Rubros')}</option>
                <option value="restaurantes">{t('subcat_restaurantes', 'Restaurantes')}</option>
                <option value="panaderias_pastelerias">{t('subcat_panaderias_pastelerias', 'Panaderías / Pastelerías')}</option>
                <option value="pizzerias">{t('subcat_pizzerias', 'Pizzerías')}</option>
                <option value="hoteles">{t('subcat_hoteles', 'Hoteles')}</option>
                <option value="food_service">{t('subcat_food_service', 'Food Service')}</option>
                <option value="supermercados">{t('subcat_supermercados', 'Supermercados')}</option>
                <option value="hipermercados">{t('subcat_hipermercados', 'Hipermercados')}</option>
                <option value="bodegones">{t('subcat_bodegones', 'Bodegones')}</option>
                <option value="abastos">{t('subcat_abastos', 'Abastos')}</option>
                <option value="tiendas_conveniencia">{t('subcat_tiendas_conveniencia', 'Tiendas de conveniencia')}</option>
                <option value="bodega">{t('subcat_bodega', 'Bodega')}</option>
                <option value="otro">{t('rubro_other', 'Otro')}</option>
              </Form.Select>
            </Col>
            <Col md={2} className="mb-2 mb-md-0">
              <Form.Select 
                value={zoneFilter} 
                onChange={e => handleZoneFilterChange(e.target.value)}
                className="customer-filter-control w-100 cursor-pointer shadow-none"
                style={zoneFilter === 'unassigned' ? { borderColor: '#ef4444', color: '#ef4444', fontWeight: 'bold' } : {}}
              >
                <option value="">{t('all_zones', 'Todas las Zonas')}</option>
                <option value="unassigned" style={{ color: '#ef4444', fontWeight: 'bold' }}>
                  {t('unassigned_zone', 'Sin Zona Asignada')}{stats.unassigned_zone !== undefined ? ` (${stats.unassigned_zone})` : ''}
                </option>
                {zones.map(z => (
                  <option key={z.id} value={z.id}>{z.name} {z.code ? `(${z.code})` : ''}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Select 
                value={statusFilter} 
                onChange={e => setStatusFilter(e.target.value)}
                className="customer-filter-control w-100 cursor-pointer shadow-none"
              >
                <option value="">{t('status_all', 'Todos los Estados')}</option>
                <option value="inactive">{t('status_inactive', 'Invitado / Sin Clave (Pre-cargado)')}</option>
                <option value="pending_approval">{t('status_pending_approval', 'Pendiente por Auditar')}</option>
                <option value="requires_visit">{t('status_requires_visit', 'Requiere Visita de Campo')}</option>
                <option value="active">{t('status_active', 'Activo / Operativo')}</option>
                <option value="disabled">{t('status_disabled', 'Deshabilitado / Bloqueado')}</option>
                <option value="rejected">{t('status_rejected', 'Rechazado / No Aprobado')}</option>
              </Form.Select>
            </Col>
          </Row>

          {zoneFilter === 'unassigned' && (
            <div className="alert alert-warning py-2 px-3 mb-3 d-flex align-items-center justify-content-between shadow-sm" style={{ borderRadius: '8px', fontSize: '0.85rem', backgroundColor: isDark ? 'rgba(234, 179, 8, 0.15)' : '#fffbeb', borderColor: 'rgba(234, 179, 8, 0.4)', color: isDark ? '#fef08a' : '#854d0e' }}>
              <div className="d-flex align-items-center">
                <i className="mdi mdi-alert-circle-outline me-2 fs-5 text-warning"></i>
                <span><strong>Filtrando clientes sin zona asignada.</strong> Estos clientes no tienen geocerca asignada o quedaron fuera de sus zonas tras la última edición territorial.</span>
              </div>
              <button className="btn btn-sm btn-link text-decoration-none fw-bold p-0 ms-2 d-inline-flex align-items-center" style={{ color: isDark ? '#ffffff' : '#010028' }} onClick={() => handleZoneFilterChange('')}>
                <i className="mdi mdi-close me-1"></i> {t('remove_filter', 'Quitar filtro')}
              </button>
            </div>
          )}

          <div className="table-responsive" style={{ width: '100%', overflowX: 'auto' }}>
            <div style={{ width: '100%' }}>
              <DataTable 
                columns={columns} 
                data={customers} 
                pagination 
                customStyles={customStyles} 
                progressPending={loading}
                progressComponent={<LoadingState title={t('loading_customers', 'Cargando directorio de clientes...')} description={t('loading_customers_sub', 'Sincronizando estado comercial y asignaciones de zona')} />}
                noDataComponent={<div className="my-5 text-muted text-center">{t('no_customers_found', 'No se encontraron clientes.')}</div>}
                paginationComponentOptions={{
                  rowsPerPageText: t('rows_per_page', 'Filas por página:'),
                  rangeSeparatorText: t('of_range', 'de'),
                  selectAllRowsItem: true,
                  selectAllRowsItemText: t('all_items', 'Todos')
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* MODAL DE INSPECCIÓN DOCUMENTAL Y AUDITORÍA */}
      <Modal show={showInspectModal} onHide={() => setShowInspectModal(false)} size="lg" centered>
        <Modal.Header closeButton closeVariant="white" style={{ backgroundColor: isDark ? '#10172d' : '#00024f', color: '#ffffff', borderBottom: '3px solid #183eeb' }}>
          <Modal.Title><i className="mdi mdi-file-search me-2"></i>Inspección Comercial y Auditoría</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ backgroundColor: isDark ? '#161e38' : '#ffffff', color: isDark ? '#f8fafc' : '#010028' }}>
          {inspectCustomer && (
            <>
              <div 
                className="p-3 rounded-3 mb-4" 
                style={{ 
                  backgroundColor: isDark ? '#10172d' : '#f8fafc', 
                  border: isDark ? '1px solid #233052' : '1px solid #e2e8f0',
                  color: isDark ? '#f8fafc' : '#010028'
                }}
              >
                <Row className="g-3">
                  <Col md={6}>
                    <div><strong>RIF / Cédula:</strong> <span style={{ color: isDark ? '#ffffff' : '#010028' }}>{inspectCustomer.rif_cedula}</span></div>
                    <div><strong>Razón Social:</strong> <span style={{ color: isDark ? '#ffffff' : '#010028' }}>{inspectCustomer.razon_social}</span></div>
                    {inspectCustomer.nombre_comercial && (
                      <div><strong>Nombre Comercial:</strong> <span style={{ color: isDark ? '#ffffff' : '#010028' }}>{inspectCustomer.nombre_comercial}</span></div>
                    )}
                    {inspectCustomer.nombre_contacto && (
                      <div><strong>Persona de Contacto:</strong> <span style={{ color: isDark ? '#ffffff' : '#010028' }}>{inspectCustomer.nombre_contacto}</span></div>
                    )}
                    <div className="mt-1"><strong>Estado Actual:</strong> {getStatusBadge(inspectCustomer.estatus, inspectCustomer.is_resubmitted, inspectCustomer.tasks)}</div>
                  </Col>
                  <Col md={6}>
                    <div><strong>Teléfono:</strong> <span style={{ color: isDark ? '#ffffff' : '#010028' }}>{inspectCustomer.telefono || 'N/A'}</span></div>
                    <div><strong>Email:</strong> <span style={{ color: isDark ? '#ffffff' : '#010028' }}>{inspectCustomer.email_facturacion || 'N/A'}</span></div>
                    <div><strong>Dirección Física:</strong> <span style={{ color: isDark ? '#ffffff' : '#010028' }}>{inspectCustomer.direccion_fisica || 'N/A'}</span></div>
                    <div>
                      <strong>Zona / Vendedor Territorial:</strong>{' '}
                      {inspectCustomer.zone ? (
                        <span style={{ color: isDark ? '#ffffff' : '#010028' }}>
                          {inspectCustomer.zone.name} {inspectCustomer.zone.code ? `(${inspectCustomer.zone.code})` : ''} — {inspectCustomer.vendedor?.nombre_completo || 'Sin vendedor titular'}
                        </span>
                      ) : (
                        <span className="text-danger fw-bold">Fuera de geocerca activa (Sin zona ni vendedor)</span>
                      )}
                    </div>
                  </Col>
                </Row>
              </div>

              {['pending_approval', 'pendiente'].includes(inspectCustomer.estatus?.toLowerCase()) && !inspectCustomer.zone_id && (
                <div
                  className="mb-4 p-3 rounded-3"
                  style={{
                    backgroundColor: isDark ? 'rgba(239, 68, 68, 0.15)' : '#fef2f2',
                    border: isDark ? '1px solid rgba(239, 68, 68, 0.4)' : '1px solid #fecaca',
                    color: isDark ? '#f87171' : '#991b1b',
                    borderRadius: '10px'
                  }}
                >
                  <div className="fw-bold mb-1 d-flex align-items-center" style={{ fontSize: '0.98rem' }}>
                    <i className="mdi mdi-map-marker-off-outline me-2"></i>
                    {t('out_of_zone_title', 'Ubicación fuera de las Zonas Comerciales Activas')}
                  </div>
                  <div className="small" style={{ color: isDark ? '#fca5a5' : '#7f1d1d' }}>
                    {t('out_of_zone_msg', 'Las coordenadas GPS registradas por el cliente no entran en ninguna geocerca activa, por lo que aún no tiene vendedor territorial asignado. Si desea asignarle visita de campo, primero amplíe el polígono de la zona correspondiente en Gestión de Zonas.')}
                  </div>
                </div>
              )}

              {/* TASK INFO IF REQUIRES_VISIT */}
              {inspectCustomer.estatus?.toLowerCase() === 'requires_visit' && (
                <div
                  className="mb-4 p-3 rounded-3 shadow-sm"
                  style={{
                    backgroundColor: isDark ? 'rgba(245, 158, 11, 0.15)' : '#fffbeb',
                    border: isDark ? '1px solid rgba(245, 158, 11, 0.4)' : '1px solid #fde68a',
                    borderLeft: '5px solid #f59e0b',
                    borderRadius: '10px'
                  }}
                >
                  <div className="fw-bold mb-1 d-flex align-items-center" style={{ fontSize: '1.02rem', color: isDark ? '#fbbf24' : '#b45309' }}>
                    <i className="mdi mdi-truck-delivery me-2"></i>{t('field_visit_in_progress', 'Visita Comercial de Campo en Proceso')}
                  </div>
                  <div style={{ color: isDark ? '#fef3c7' : '#92400e' }}>
                    <strong>{t('seller_notified', 'Vendedor Titular Notificado:')}</strong> {inspectCustomer.vendedor?.nombre_completo || 'Vendedor Asignado'}
                  </div>
                  {inspectCustomer.zone && (
                    <div style={{ color: isDark ? '#fef3c7' : '#92400e' }}>
                      <strong>{t('territorial_zone', 'Zona Territorial:')}</strong> {inspectCustomer.zone.name} {inspectCustomer.zone.code ? `(${inspectCustomer.zone.code})` : ''}
                    </div>
                  )}
                  {inspectCustomer.tasks && inspectCustomer.tasks.length > 0 && (
                    <div className="mt-1 small" style={{ color: isDark ? '#fde68a' : '#78350f' }}>
                      <strong>{t('instructions_sent', 'Instrucciones enviadas:')}</strong> {inspectCustomer.tasks[0].notas}
                    </div>
                  )}
                </div>
              )}

              {(() => {
                const completedTask = getLatestCompletedFieldTask(inspectCustomer);
                if (!completedTask) return null;

                const isVisitApproved = completedTask.resultado === 'approved';
                const accentColor = isVisitApproved ? '#16a34a' : '#dc2626';
                const motivos = Array.isArray(completedTask.motivos_rechazo)
                  ? completedTask.motivos_rechazo.filter(Boolean)
                  : [];
                const photoUrl = completedTask.evidencia_fachada_url
                  ? getDocumentUrl(completedTask.evidencia_fachada_url)
                  : null;
                const sellerLabel = completedTask.vendedor?.nombre_completo
                  || inspectCustomer.vendedor?.nombre_completo
                  || 'Vendedor de Zona';
                const completedDateStr = completedTask.completed_at
                  ? new Date(completedTask.completed_at).toLocaleString('es-VE', {
                      dateStyle: 'medium',
                      timeStyle: 'short'
                    })
                  : null;

                return (
                  <div
                    className="p-3 mb-4 rounded-3 shadow-sm"
                    style={{
                      backgroundColor: isDark ? '#10172d' : (isVisitApproved ? '#f0fdf4' : '#fef2f2'),
                      border: `1px solid ${isDark ? '#233052' : (isVisitApproved ? '#bbf7d0' : '#fecaca')}`,
                      borderLeft: `5px solid ${accentColor}`,
                      color: isDark ? '#f8fafc' : '#010028'
                    }}
                  >
                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                      <h6 className="fw-bold mb-0 d-flex align-items-center" style={{ color: accentColor }}>
                        <i className={`mdi ${isVisitApproved ? 'mdi-check-decagram' : 'mdi-alert-octagon'} me-2`} style={{ fontSize: '1.25rem' }}></i>
                        {t('field_verification_report_title', 'Reporte de Visita Comercial de Campo')}
                      </h6>
                      <Badge style={{ backgroundColor: accentColor, color: '#ffffff', fontSize: '0.78rem' }} className="px-3 py-2 fw-bold">
                        {isVisitApproved ? t('badge_validated_by_seller', 'VALIDADO POR VENDEDOR') : t('badge_observed_rejection', 'OBSERVADO / MOTIVOS DE RECHAZO')}
                      </Badge>
                    </div>

                    <Row className="g-3 align-items-start">
                      <Col md={photoUrl ? 7 : 12}>
                        <div className="small mb-2">
                          <strong>{t('inspected_by', 'Inspeccionado por:')}</strong> {sellerLabel}
                          {completedDateStr && <span className="ms-2 text-muted">({completedDateStr})</span>}
                        </div>

                        {isVisitApproved ? (
                          <div
                            className="p-2 rounded mb-2 small fw-semibold"
                            style={{
                              backgroundColor: isDark ? 'rgba(22, 163, 74, 0.15)' : '#dcfce7',
                              color: isDark ? '#4ade80' : '#166534'
                            }}
                          >
                            <i className="mdi mdi-checkbox-marked-circle-outline me-1"></i>
                            {t('physical_confirmation_note', 'Confirmación física: El vendedor verificó en sitio que el establecimiento existe, está operativo y cumple condiciones comerciales.')}
                          </div>
                        ) : (
                          <div
                            className="p-2 rounded mb-2 small"
                            style={{
                              backgroundColor: isDark ? 'rgba(220, 38, 38, 0.15)' : '#fee2e2',
                              color: isDark ? '#f87171' : '#991b1b'
                            }}
                          >
                            <div className="fw-bold mb-1">{t('rejection_reasons_in_field', 'Motivos de rechazo marcados en campo:')}</div>
                            {motivos.length > 0 ? (
                              <ul className="mb-0 ps-3">
                                {motivos.map((m, idx) => (
                                  <li key={idx} className="fw-semibold">{m}</li>
                                ))}
                              </ul>
                            ) : (
                              <div>{t('no_specific_reasons', 'No se detallaron motivos específicos.')}</div>
                            )}
                          </div>
                        )}

                        {completedTask.observaciones_vendedor && (
                          <div className="small mt-2">
                            <strong>{t('seller_observations', 'Observaciones del Vendedor:')}</strong>{' '}
                            <span>{completedTask.observaciones_vendedor}</span>
                          </div>
                        )}

                        {completedTask.check_lat && completedTask.check_lng && (
                          <div className="mt-2">
                            <a
                              href={`https://www.google.com/maps/search/?api=1&query=${completedTask.check_lat},${completedTask.check_lng}`}
                              target="_blank"
                              rel="noreferrer"
                              className="btn btn-sm btn-outline-primary py-1 px-2"
                              style={{ fontSize: '0.76rem', borderRadius: '6px' }}
                            >
                              <i className="mdi mdi-crosshairs-gps me-1"></i>
                              {t('view_gps_capture_point', 'Ver punto GPS de captura')} ({Number(completedTask.check_lat).toFixed(5)}, {Number(completedTask.check_lng).toFixed(5)})
                            </a>
                          </div>
                        )}
                      </Col>

                      {photoUrl && (
                        <Col md={5}>
                          <div className="border rounded p-2 text-center" style={{ backgroundColor: isDark ? '#161e38' : '#ffffff', borderColor: isDark ? '#233052' : '#e2e8f0', borderRadius: '10px' }}>
                            <div className="small fw-bold mb-2" style={{ color: isDark ? '#ffffff' : '#010028' }}>{t('facade_photo_title', 'Evidencia Fotográfica de Fachada')}</div>
                            <div
                              onClick={() => setPreviewImage({ url: photoUrl, title: `${t('zoom_facade_title', 'Fachada del Comercio')} — ${inspectCustomer.nombre_comercial || inspectCustomer.razon_social}` })}
                              style={{ cursor: 'pointer' }}
                              title="Haz clic para abrir foto en tamaño completo"
                            >
                              <img
                                src={photoUrl}
                                alt="Fachada del comercio"
                                style={{
                                  width: '100%',
                                  maxHeight: '190px',
                                  objectFit: 'cover',
                                  borderRadius: '8px',
                                  border: isDark ? '1px solid #334155' : '1px solid rgba(0,0,0,0.1)'
                                }}
                              />
                            </div>
                            <div className="mt-2">
                              <Button
                                variant="link"
                                size="sm"
                                onClick={() => setPreviewImage({ url: photoUrl, title: `${t('zoom_facade_title', 'Fachada del Comercio')} — ${inspectCustomer.nombre_comercial || inspectCustomer.razon_social}` })}
                                className="small fw-bold text-decoration-none p-0"
                                style={{ color: isDark ? '#60a5fa' : '#183eeb' }}
                              >
                                <i className="mdi mdi-magnify-plus-outline me-1"></i>{t('expand_photo', 'Ampliar Fotografía')}
                              </Button>
                            </div>
                          </div>
                        </Col>
                      )}
                    </Row>
                  </div>
                );
              })()}

              {/* PERFIL COMERCIAL Y SEGMENTACIÓN (HU-SEG-01 / FE-04) */}
              <div 
                className="p-3 mb-4 rounded-3" 
                style={{ 
                  backgroundColor: isDark ? '#10172d' : '#f8fafc', 
                  border: isDark ? '1px solid #233052' : '1px solid #e2e8f0', 
                  borderLeft: `4px solid ${CANAL_CONFIG[inspectCustomer.canal_comercial]?.color || '#183eeb'}`,
                  color: isDark ? '#f8fafc' : '#010028'
                }}
              >
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="fw-bold mb-0 d-flex align-items-center" style={{ color: CANAL_CONFIG[inspectCustomer.canal_comercial]?.color || (isDark ? '#60a5fa' : '#183eeb') }}>
                    <i className="mdi mdi-store-check-outline me-2" style={{ fontSize: '1.2rem' }}></i>
                    {t('onboarding_commercial_profile', 'Clasificación Comercial y Segmentación (Onboarding)')}
                  </h6>
                  {inspectCustomer.requires_segmentation ? (
                    <Badge bg="warning" className="text-dark px-2 py-1 d-inline-flex align-items-center gap-1">
                      <i className="mdi mdi-clock-outline"></i>
                      {t('pending_onboarding', 'Pendiente de Onboarding')}
                    </Badge>
                  ) : (
                    <Badge bg="success" className="px-2 py-1 d-inline-flex align-items-center gap-1">
                      <i className="mdi mdi-check"></i>
                      {t('segmented_done', 'Segmentado')}
                    </Badge>
                  )}
                </div>
                <Row className="g-3 pt-2">
                  <Col md={4}>
                    <div className="small" style={{ color: isDark ? '#94a3b8' : '#64748b' }}>{t('field_canal_comercial', 'Canal Comercial:')}</div>
                    {inspectCustomer.canal_comercial && CANAL_CONFIG[inspectCustomer.canal_comercial] ? (
                      <Badge 
                        style={{ 
                          backgroundColor: CANAL_CONFIG[inspectCustomer.canal_comercial].color,
                          fontSize: '0.8rem',
                          fontWeight: '600'
                        }}
                      >
                        {getCanalLabel(inspectCustomer.canal_comercial, t)}
                      </Badge>
                    ) : (
                      <span className="fw-bold text-muted small">{t('no_channel_assigned', 'Sin canal asignado')}</span>
                    )}
                  </Col>
                  <Col md={4}>
                    <div className="small" style={{ color: isDark ? '#94a3b8' : '#64748b' }}>{t('subcat_trade_label', 'Subcategoría Comercial / Rubro:')}</div>
                    <div className="fw-bold text-capitalize" style={{ color: isDark ? '#ffffff' : '#010028' }}>
                      {inspectCustomer.subcategoria_comercial 
                        ? (getSubcatLabel(inspectCustomer.canal_comercial, inspectCustomer.subcategoria_comercial, t) || inspectCustomer.subcategoria_comercial)
                        : (inspectCustomer.rubro || inspectCustomer.categoria || t('undefined_text', 'Sin definir'))}
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="small" style={{ color: isDark ? '#94a3b8' : '#64748b' }}>{t('assigned_credit_limit_label', 'Límite de Compra / Crédito Asignado:')}</div>
                    <div className="fw-bold text-success" style={{ fontSize: '1.05rem' }}>
                      ${inspectCustomer.limite_credito ? Number(inspectCustomer.limite_credito).toLocaleString('en-US', { minimumFractionDigits: 2 }) : (CANAL_CONFIG[inspectCustomer.canal_comercial]?.limite ? `${CANAL_CONFIG[inspectCustomer.canal_comercial].limite}.00` : '0.00')} USD
                      <span className="badge rounded-pill bg-light text-dark border ms-2" style={{ fontSize: '0.75rem', verticalAlign: 'middle' }}>
                        {inspectCustomer.dias_gracia !== undefined && inspectCustomer.dias_gracia !== null ? inspectCustomer.dias_gracia : (CANAL_CONFIG[inspectCustomer.canal_comercial]?.dias_credito || 8)} {t('credit_days_badge', 'días')}
                      </span>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="small" style={{ color: isDark ? '#94a3b8' : '#64748b' }}>{t('catalog_view_configured', 'Vista de Catálogo Configurada:')}</div>
                    <div className="fw-bold" style={{ color: isDark ? '#60a5fa' : '#183eeb' }}>
                      <i className={`mdi ${inspectCustomer.canal_comercial === 'transformadores' ? 'mdi-factory' : 'mdi-store'} me-1`}></i>
                      {getCatalogViewLabel(inspectCustomer.canal_comercial, t)}
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="small" style={{ color: isDark ? '#94a3b8' : '#64748b' }}>{t('top_interest_categories', 'Categorías de Mayor Interés:')}</div>
                    <div className="d-flex flex-wrap gap-1 mt-1">
                      {inspectCustomer.categorias_interes && Array.isArray(inspectCustomer.categorias_interes) && inspectCustomer.categorias_interes.length > 0 ? (
                        inspectCustomer.categorias_interes.map((cat, idx) => (
                          <Badge key={idx} bg="primary" style={{ fontSize: '0.72rem' }}>{cat}</Badge>
                        ))
                      ) : (
                        <span className="small" style={{ color: isDark ? '#94a3b8' : '#64748b' }}>{t('no_registered_categories', 'Sin categorías registradas')}</span>
                      )}
                    </div>
                  </Col>
                </Row>
              </div>

              <h6 className="fw-bold mb-3" style={{ color: isDark ? '#60a5fa' : '#183eeb' }}>
                <i className="mdi mdi-folder-multiple-image me-2"></i>{t('digital_documents_attached', 'Documentos Digitales Adjuntos')} ({inspectCustomer.documents?.length || 0})
              </h6>

              {inspectCustomer.documents && inspectCustomer.documents.length > 0 ? (
                <Row className="g-3 mb-4">
                  {inspectCustomer.documents.map(doc => (
                    <Col md={4} key={doc.id}>
                      <Card 
                        className="h-100 shadow-sm" 
                        style={{ 
                          borderRadius: '10px',
                          backgroundColor: isDark ? '#10172d' : '#ffffff',
                          border: isDark ? '1px solid #233052' : '1px solid #e2e8f0',
                          color: isDark ? '#f8fafc' : '#010028'
                        }}
                      >
                        <Card.Body className="text-center p-3">
                          <i className="mdi mdi-file-document-outline text-primary mb-2" style={{ fontSize: '2.5rem' }}></i>
                          <div className="small fw-bold text-truncate" style={{ color: isDark ? '#ffffff' : '#010028' }} title={doc.original_name}>{doc.original_name || doc.tipo_documento}</div>
                          <Badge bg="secondary" className="mb-2">{doc.tipo_documento}</Badge>
                          <div>
                            <Button 
                              variant="link" 
                              size="sm" 
                              href={getDocumentUrl(doc.file_path)} 
                              target="_blank" 
                              rel="noreferrer" 
                              className="text-decoration-none p-0 fw-bold"
                            >
                              <i className="mdi mdi-open-in-new me-1"></i>Ver Archivo
                            </Button>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
              ) : (
                <div 
                  className="mb-4 p-3 rounded-3 d-flex align-items-center" 
                  style={{ 
                    borderRadius: '10px',
                    backgroundColor: isDark ? 'rgba(56, 189, 248, 0.12)' : '#e0f2fe',
                    color: isDark ? '#38bdf8' : '#0369a1',
                    border: isDark ? '1px solid rgba(56, 189, 248, 0.25)' : '1px solid #bae6fd'
                  }}
                >
                  <i className="mdi mdi-information-outline me-2 fs-5"></i>
                  <span>No hay documentos adjuntos digitales cargados para este registro.</span>
                </div>
              )}

              {/* DIRECCIONES DE DESPACHO Y UBICACIÓN GPS (HU38 / FE-01) */}
              <div 
                className="mb-4 p-3 rounded-3" 
                style={{ 
                  backgroundColor: isDark ? '#10172d' : '#f8fafc',
                  border: isDark ? '1px solid #233052' : '1px solid #e2e8f0',
                  color: isDark ? '#f8fafc' : '#010028'
                }}
              >
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="fw-bold mb-0 d-flex align-items-center" style={{ color: isDark ? '#60a5fa' : '#183eeb' }}>
                    <i className="mdi mdi-map-marker-multiple-outline me-2" style={{ fontSize: '1.2rem' }}></i>
                    Direcciones de Despacho y Ubicación GPS ({customerAddresses.length || 1})
                  </h6>
                  <Button 
                    variant="outline-primary" 
                    size="sm" 
                    style={{ borderRadius: '8px', fontSize: '0.78rem' }}
                    onClick={() => {
                      setShowInspectModal(false);
                      handleOpenEditModal(inspectCustomer);
                    }}
                  >
                    <i className="mdi mdi-pencil me-1"></i>Editar Coordenadas / Cliente
                  </Button>
                </div>

                {customerAddresses.length > 0 ? (
                  <div className="table-responsive mb-3 border rounded" style={{ borderRadius: '8px', overflow: 'hidden' }}>
                    <Table size="sm" bordered className="align-middle customer-table mb-0">
                      <thead>
                        <tr>
                          <th>Estado / Indicador</th>
                          <th>Dirección de Entrega / Calle</th>
                          <th>Sector / Referencia</th>
                          <th>Coordenadas GPS</th>
                          <th className="text-center">Mapa</th>
                        </tr>
                      </thead>
                      <tbody>
                        {customerAddresses.map((addr, idx) => (
                          <tr 
                            key={addr.id || idx} 
                            className={`customer-table-row ${selectedAddressIndex === idx ? 'table-active-row' : ''}`}
                            style={{ cursor: 'pointer' }}
                            onClick={() => setSelectedAddressIndex(idx)}
                          >
                            <td>
                              {addr.is_principal ? (
                                <Badge bg="success" className="px-2 py-1"><i className="mdi mdi-check-circle me-1"></i>Principal</Badge>
                              ) : (
                                <Badge bg="secondary" className="px-2 py-1">Secundaria</Badge>
                              )}
                            </td>
                            <td className="fw-bold">{addr.direccion_completa || addr.calle_avenida}</td>
                            <td>{addr.sector ? `${addr.sector} - ${addr.punto_referencia || ''}` : (addr.punto_referencia || 'N/A')}</td>
                            <td>
                              <code>{addr.latitude && addr.longitude ? `${addr.latitude}, ${addr.longitude}` : (addr.ubicacion_gps || 'Sin GPS')}</code>
                            </td>
                            <td className="text-center">
                              <Button 
                                variant={selectedAddressIndex === idx ? "primary" : "outline-primary"} 
                                size="sm" 
                                style={{ borderRadius: '6px', fontSize: '0.78rem' }}
                                onClick={(e) => { e.stopPropagation(); setSelectedAddressIndex(idx); }}
                              >
                                <i className="mdi mdi-crosshairs-gps me-1"></i>Ver Pin
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                ) : (
                  <Alert variant="secondary" className="mb-3">
                    <i className="mdi mdi-map-marker-off me-2"></i>
                    Dirección Física Registrada: <strong>{inspectCustomer.direccion_fisica || 'No especificada'}</strong> 
                    {inspectCustomer.ubicacion_gps && <span> (GPS: <code>{inspectCustomer.ubicacion_gps}</code>)</span>}
                  </Alert>
                )}

                {/* MARCADOR Y MAPA INTERACTIVO GPS */}
                {(() => {
                  const targetAddr = customerAddresses[selectedAddressIndex] || {
                    latitude: inspectCustomer.ubicacion_gps ? parseFloat(inspectCustomer.ubicacion_gps.split(',')[0]) : 10.0678,
                    longitude: inspectCustomer.ubicacion_gps ? parseFloat(inspectCustomer.ubicacion_gps.split(',')[1]) : -69.3456,
                  };
                  const lat = targetAddr.latitude || (inspectCustomer.ubicacion_gps ? parseFloat(inspectCustomer.ubicacion_gps.split(',')[0]) : 10.0678);
                  const lng = targetAddr.longitude || (inspectCustomer.ubicacion_gps ? parseFloat(inspectCustomer.ubicacion_gps.split(',')[1]) : -69.3456);

                  if (!isNaN(lat) && !isNaN(lng)) {
                    return (
                      <div className="border rounded overflow-hidden shadow-sm" style={{ borderRadius: '10px' }}>
                        <div className="p-2 bg-dark text-white small d-flex justify-content-between align-items-center">
                          <span><i className="mdi mdi-map-marker-radius me-1 text-warning"></i>Marcador GPS en Vivo: <strong>Lat {lat}, Lng {lng}</strong></span>
                          <a 
                            href={`https://www.google.com/maps/search/?api=1&query=${lat},${lng}`} 
                            target="_blank" 
                            rel="noreferrer" 
                            className="btn btn-sm btn-outline-light py-0 px-2 style-none"
                            style={{ fontSize: '0.75rem' }}
                          >
                            Abrir en Google Maps <i className="mdi mdi-open-in-new ms-1"></i>
                          </a>
                        </div>
                        <iframe
                          title="GPS Customer Map"
                          width="100%"
                          height="200"
                          frameBorder="0"
                          scrolling="no"
                          marginHeight="0"
                          marginWidth="0"
                          src={`https://www.openstreetmap.org/export/embed.html?bbox=${lng - 0.008},${lat - 0.008},${lng + 0.008},${lat + 0.008}&layer=mapnik&marker=${lat},${lng}`}
                        ></iframe>
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>

              <div className="d-flex justify-content-between align-items-center pt-3 border-top">
                {['pending_approval', 'pendiente', 'requires_visit'].includes(inspectCustomer.estatus?.toLowerCase()) ? (
                  <>
                    <Button 
                      variant="outline-danger" 
                      style={{ borderRadius: '10px' }}
                      onClick={() => handleOpenRejectModal(inspectCustomer)}
                    >
                      <i className="mdi mdi-close-circle me-1"></i>{t('btn_reject_request', 'Rechazar Solicitud')}
                    </Button>
                    <div>
                      {inspectCustomer.estatus?.toLowerCase() !== 'requires_visit' && (
                        <Button 
                          variant="warning" 
                          className="me-2 text-dark font-weight-bold" 
                          style={{ borderRadius: '10px' }}
                          onClick={() => handleOpenVisitModal(inspectCustomer)}
                        >
                          <i className="mdi mdi-truck-delivery me-1"></i>{t('btn_request_vendor_visit', 'Solicitar Visita del Vendedor')}
                        </Button>
                      )}
                      <Button 
                        variant="success" 
                        style={{ borderRadius: '10px' }}
                        onClick={() => handleOpenApproveModal(inspectCustomer)}
                      >
                        <i className="mdi mdi-check-decagram me-1"></i>{t('btn_approve_commercial_account', 'Aprobar Cuenta Comercial')}
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <Button 
                      variant="secondary" 
                      style={{ borderRadius: '10px' }}
                      onClick={() => setShowInspectModal(false)}
                    >
                      <i className="mdi mdi-close me-1"></i>{t('btn_close', 'Cerrar')}
                    </Button>
                    <Button 
                      variant="primary" 
                      style={{ borderRadius: '10px', backgroundColor: '#183eeb', borderColor: '#183eeb' }}
                      onClick={() => {
                        setShowInspectModal(false);
                        handleOpenEditModal(inspectCustomer);
                      }}
                    >
                      <i className="mdi mdi-pencil me-1"></i>{t('btn_edit_customer_data', 'Editar Datos del Cliente')}
                    </Button>
                  </>
                )}
              </div>
            </>
          )}
        </Modal.Body>
      </Modal>

      {/* MODAL PARA SOLICITAR VISITA DEL VENDEDOR (requires_visit) */}
      <Modal show={showVisitModal} onHide={() => setShowVisitModal(false)} centered>
        {(() => {
          const resolvedZoneId = inspectCustomer?.zone_id || inspectCustomer?.zone?.id || '';
          const resolvedZone = inspectCustomer?.zone || zones.find(z => String(z.id) === String(resolvedZoneId)) || null;
          const resolvedVendorId = resolvedZone?.vendedor_id || inspectCustomer?.vendedor_id || inspectCustomer?.vendedor?.id || '';
          const resolvedVendor = resolvedZone?.vendedor
            || (inspectCustomer?.vendedor && String(inspectCustomer.vendedor.id) === String(resolvedVendorId) ? inspectCustomer.vendedor : null)
            || vendors.find(v => String(v.id) === String(resolvedVendorId))
            || null;
          const canDispatchFieldVisit = Boolean(resolvedZoneId && resolvedVendorId);

          return (
            <Form onSubmit={handleConfirmVisitRequest}>
              <Modal.Header closeButton closeVariant="white" style={{ backgroundColor: isDark ? '#10172d' : '#fd7e14', color: '#ffffff' }}>
                <Modal.Title className="h5 fw-bold">
                  <i className="mdi mdi-truck-delivery me-2"></i>
                  {t('assign_field_visit_title', 'Solicitar Visita de Campo al Vendedor')}
                </Modal.Title>
              </Modal.Header>
              <Modal.Body style={{ backgroundColor: isDark ? '#161e38' : '#ffffff', color: isDark ? '#f8fafc' : '#010028' }}>
                {!resolvedZoneId ? (
                  <Alert variant="danger" className="mb-0" style={{ borderRadius: '10px' }}>
                    <div className="fw-bold mb-1">
                      <i className="mdi mdi-map-marker-off-outline me-2"></i>
                      Cliente fuera de Zona Comercial
                    </div>
                    <div className="small">
                      Este cliente no pertenece a ninguna zona geográfica activa según sus coordenadas GPS. Para evitar asignar visitas a vendedores lejanos, primero ajuste el polígono de la geocerca correspondiente en <strong>Gestión de Zonas</strong> para que el sistema asigne automáticamente al vendedor titular cercano.
                    </div>
                  </Alert>
                ) : !resolvedVendorId ? (
                  <Alert variant="warning" className="mb-0" style={{ borderRadius: '10px' }}>
                    <div className="fw-bold mb-1">
                      <i className="mdi mdi-account-alert-outline me-2"></i>
                      Zona sin Vendedor Titular
                    </div>
                    <div className="small">
                      El cliente se encuentra dentro de la zona <strong>{resolvedZone?.name || 'Asignada'}</strong>, pero dicha zona aún no tiene un vendedor titular asignado. Asigne primero el vendedor titular a la zona antes de enviar la visita de campo.
                    </div>
                  </Alert>
                ) : (
                  <>
                    <p className="small text-muted mb-3">
                      {t('visit_status_notice', 'Al confirmar, el cliente pasará a Requiere Visita de Campo y se enviará una notificación inmediata al vendedor titular de su zona geográfica.')}
                    </p>

                    <div
                      className="p-3 mb-3 rounded-3"
                      style={{
                        backgroundColor: isDark ? '#10172d' : '#fff7ed',
                        border: isDark ? '1px solid #233052' : '1px solid #fed7aa'
                      }}
                    >
                      <div className="small text-muted mb-1">Asignación Territorial Automática (Geocerca GPS)</div>
                      <div className="mb-1">
                        <strong>Zona Geográfica:</strong>{' '}
                        <span>{resolvedZone?.name} {resolvedZone?.code ? `(${resolvedZone.code})` : ''}</span>
                      </div>
                      <div>
                        <strong>Vendedor Titular:</strong>{' '}
                        <span>{resolvedVendor?.nombre_completo || 'Vendedor Titular de Zona'}</span>
                        {resolvedVendor?.email && <span className="text-muted small ms-1">({resolvedVendor.email})</span>}
                      </div>
                    </div>

                    <Form.Group className="mb-2">
                      <Form.Label className="fw-bold">{t('field_instructions_vendor', 'Notas o Instrucciones para el Vendedor (Opcional)')}</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        placeholder="Ej: Contactar previamente al cliente, inspeccionar fachada del establecimiento y confirmar actividad comercial."
                        value={visitData.notas}
                        onChange={e => setVisitData({ ...visitData, notas: e.target.value })}
                        style={{
                          borderRadius: '10px',
                          border: '1px solid #183eeb',
                          backgroundColor: isDark ? '#10172d' : '#ffffff',
                          color: isDark ? '#f8fafc' : '#010028'
                        }}
                      />
                    </Form.Group>
                  </>
                )}
              </Modal.Body>
              <Modal.Footer style={{ backgroundColor: isDark ? '#10172d' : '#f8f9fc', borderTop: isDark ? '1px solid #233052' : '1px solid #dee2e6' }}>
                <Button variant="secondary" onClick={() => setShowVisitModal(false)} style={{ borderRadius: '10px' }}>
                  {t('btn_cancel', 'Cancelar')}
                </Button>
                <Button
                  type="submit"
                  style={{ backgroundColor: '#fd7e14', borderColor: '#fd7e14', color: 'white', borderRadius: '10px' }}
                  disabled={processing || !canDispatchFieldVisit}
                  className="fw-bold"
                >
                  {processing ? <Spinner size="sm" animation="border" /> : t('btn_create_visit_task', 'Notificar Visita al Vendedor')}
                </Button>
              </Modal.Footer>
            </Form>
          );
        })()}
      </Modal>

      {/* MODAL DE APROBACIÓN */}
      <Modal show={showApproveModal} onHide={() => setShowApproveModal(false)} centered>
        <Form onSubmit={handleConfirmApprove}>
          <Modal.Header closeButton closeVariant="white" style={{ backgroundColor: isDark ? '#10172d' : '#198754', color: '#ffffff' }}>
            <Modal.Title className="h5 fw-bold"><i className="mdi mdi-check-decagram me-2"></i>{t('btn_approve_commercial_account', 'Aprobar Cuenta Comercial')}</Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ backgroundColor: isDark ? '#161e38' : '#ffffff', color: isDark ? '#f8fafc' : '#010028' }}>
            <p className="small mb-3" style={{ color: isDark ? '#94a3b8' : '#64748b' }}>
              {t('approve_description', 'Estás a punto de aprobar este registro. Su cuenta de usuario será activada para ingresar a la App Móvil.')}
            </p>
            {inspectCustomer?.canal_comercial && getCanalCfg(inspectCustomer.canal_comercial) && (
              <div 
                className="p-2 mb-3 rounded d-flex justify-content-between align-items-center" 
                style={{ 
                  backgroundColor: isDark ? 'rgba(22, 163, 74, 0.15)' : '#f0fdf4', 
                  border: isDark ? '1px solid rgba(22, 163, 74, 0.4)' : '1px solid #bbf7d0',
                  color: isDark ? '#f8fafc' : '#010028'
                }}
              >
                <div>
                  <span className="small d-block" style={{ color: isDark ? '#94a3b8' : '#64748b' }}>{t('detected_channel', 'Canal Comercial Configurado:')}</span>
                  <strong className={isDark ? 'text-white' : 'text-dark'}>{getCanalLabel(inspectCustomer.canal_comercial, t)}</strong>
                </div>
                <Badge style={{ backgroundColor: getCanalCfg(inspectCustomer.canal_comercial).color, fontSize: '0.8rem' }}>
                  {t('suggested_limit', `Sugerido: $${getCanalCfg(inspectCustomer.canal_comercial).limite} USD`, { amount: getCanalCfg(inspectCustomer.canal_comercial).limite })}
                </Badge>
              </div>
            )}
            <Row>
              <Col md={7}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold">{t('field_initial_credit_limit', 'Límite de Crédito Inicial ($) *')}</Form.Label>
                  <Form.Control 
                    type="number" 
                    step="0.01" 
                    min="0" 
                    required 
                    value={approveData.limite_credito} 
                    onChange={e => setApproveData({ ...approveData, limite_credito: e.target.value })}
                    style={{ 
                      borderRadius: '10px', 
                      border: isDark ? '1px solid #334155' : '1px solid #183eeb', 
                      fontSize: '1.2rem',
                      backgroundColor: isDark ? '#10172d' : '#ffffff',
                      color: isDark ? '#ffffff' : '#010028'
                    }} 
                  />
                </Form.Group>
              </Col>
              <Col md={5}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold">{t('field_dias_credito', 'Días de Crédito *')}</Form.Label>
                  <Form.Control 
                    type="number" 
                    min="1" 
                    max="180" 
                    required 
                    value={approveData.dias_gracia ?? 8} 
                    onChange={e => setApproveData({ ...approveData, dias_gracia: parseInt(e.target.value, 10) || 0 })}
                    style={{ 
                      borderRadius: '10px', 
                      border: isDark ? '1px solid #334155' : '1px solid #183eeb', 
                      fontSize: '1.2rem',
                      backgroundColor: isDark ? '#10172d' : '#ffffff',
                      color: isDark ? '#ffffff' : '#010028'
                    }} 
                  />
                  <Form.Text className="text-muted" style={{ fontSize: '0.75rem' }}>
                    {t('dias_credito_help', 'Plazo de gracia por factura')}
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer style={{ backgroundColor: isDark ? '#10172d' : '#f8f9fc', borderTop: isDark ? '1px solid #233052' : '1px solid #dee2e6' }}>
            <Button variant="secondary" onClick={() => setShowApproveModal(false)} style={{ borderRadius: '10px' }}>{t('btn_cancel', 'Cancelar')}</Button>
            <Button type="submit" variant="success" disabled={processing} className="fw-bold" style={{ borderRadius: '10px' }}>
              {processing ? <Spinner size="sm" animation="border" /> : t('btn_confirm_approval', 'Confirmar Aprobación')}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* MODAL DE RECHAZO */}
      <Modal show={showRejectModal} onHide={() => setShowRejectModal(false)} centered>
        <Form onSubmit={handleConfirmReject}>
          <Modal.Header closeButton closeVariant="white" style={{ backgroundColor: isDark ? '#10172d' : '#dc3545', color: '#ffffff' }}>
            <Modal.Title className="h5 fw-bold"><i className="mdi mdi-close-circle me-2"></i>{t('reject_request_title', 'Rechazar Solicitud Comercial')}</Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ backgroundColor: isDark ? '#161e38' : '#ffffff', color: isDark ? '#f8fafc' : '#010028' }}>
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">{t('field_rejection_reason', 'Motivo del Rechazo *')}</Form.Label>
              <Form.Control 
                as="textarea" 
                rows={3} 
                required
                placeholder="Ej: RIF falso o negocio inexistente."
                value={rejectData.motivo}
                onChange={e => setRejectData({ ...rejectData, motivo: e.target.value })}
                style={{ 
                  borderRadius: '10px', 
                  border: isDark ? '1px solid #7f1d1d' : '1px solid #dc3545',
                  backgroundColor: isDark ? '#10172d' : '#ffffff',
                  color: isDark ? '#ffffff' : '#010028'
                }}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer style={{ backgroundColor: isDark ? '#10172d' : '#f8f9fc', borderTop: isDark ? '1px solid #233052' : '1px solid #dee2e6' }}>
            <Button variant="secondary" onClick={() => setShowRejectModal(false)} style={{ borderRadius: '10px' }}>{t('btn_cancel', 'Cancelar')}</Button>
            <Button type="submit" variant="danger" disabled={processing} className="fw-bold" style={{ borderRadius: '10px' }}>
              {processing ? <Spinner size="sm" animation="border" /> : t('btn_confirm_rejection', 'Confirmar Rechazo')}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* CREATE/EDIT/IMPORT MODAL (WITH COMPLETE ORIGINAL STYLING, TEMPLATE DOWNLOAD BAR & DRAG-AND-DROP) */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered noValidate>
        <Form onSubmit={handleSaveCustomer} noValidate>
          <Modal.Header closeButton closeVariant="white" style={{ backgroundColor: '#00024f', color: '#ffffff', borderBottom: '3px solid #183eeb' }}>
            <Modal.Title>{(editingCustomer || editingId) ? t('modal_edit_customer', 'Editar Cliente') : t('modal_new_customers', 'Cargar Nuevos Clientes')}</Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ backgroundColor: isDark ? '#161e38' : '#ffffff', color: isDark ? '#f8fafc' : '#010028', padding: '0' }}>
            {validationError && (
              <Alert variant="danger" className="m-3 font-weight-bold d-flex align-items-center shadow-sm" style={{ borderRadius: '10px' }}>
                <i className="mdi mdi-alert-circle me-2" style={{ fontSize: '1.2rem' }}></i>
                <span>{validationError}</span>
              </Alert>
            )}

            {!(editingCustomer || editingId) ? (
              <Tabs
                activeKey={modalTab}
                onSelect={(k) => { setModalTab(k); setValidationError(''); setInvalidFields({}); }}
                className="mb-3 border-bottom-0 pt-3 px-3"
                style={{ backgroundColor: '#f8f9fa' }}
              >
                <Tab eventKey="manual" title={<><i className="mdi mdi-cursor-text me-1"></i> {t('tab_manual_creation', 'Creación Manual')}</>}>
                  <div className="p-3">
                    <div className="alert alert-info" style={{ borderRadius: '10px' }}>
                      <i className="mdi mdi-information-outline me-2"></i>
                      {t('manual_alert_notice', 'Este cliente se registrará en estado Invitado / Sin Clave (inactive). Cuando ingrese su RIF en la App Móvil, activará su cuenta rápidamente creando su contraseña sin requerir aprobación adicional.')}
                    </div>
                    
                    <div className="row">
                      <div className="col-md-6">
                        <Form.Group className="mb-3">
                          <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>{t('field_rif', 'RIF o Cédula')} *</Form.Label>
                          <Form.Control 
                            type="text" 
                            required 
                            isInvalid={!!invalidFields.rif_cedula}
                            value={formData.rif_cedula} 
                            onChange={e => {
                              setFormData({...formData, rif_cedula: e.target.value});
                              if (invalidFields.rif_cedula) setInvalidFields({...invalidFields, rif_cedula: false});
                            }} 
                            onBlur={handleRifBlur}
                            placeholder="J-12345678-9" 
                            style={{ border: invalidFields.rif_cedula ? '2px solid #dc3545' : '1px solid #183eeb', borderRadius: '10px' }} 
                          />
                          {invalidFields.rif_cedula && (
                            <Form.Control.Feedback type="invalid" className="d-block text-danger fw-bold mt-1">
                              {typeof invalidFields.rif_cedula === 'string' ? invalidFields.rif_cedula : 'El RIF o Cédula es obligatorio.'}
                            </Form.Control.Feedback>
                          )}
                        </Form.Group>
                      </div>
                      <div className="col-md-6">
                        <Form.Group className="mb-3">
                          <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>{t('field_razon_social', 'Razón Social')} *</Form.Label>
                          <Form.Control 
                            type="text" 
                            required 
                            isInvalid={!!invalidFields.razon_social}
                            value={formData.razon_social} 
                            onChange={e => {
                              setFormData({...formData, razon_social: e.target.value});
                              if (invalidFields.razon_social) setInvalidFields({...invalidFields, razon_social: false});
                            }} 
                            style={{ border: invalidFields.razon_social ? '2px solid #dc3545' : '1px solid #183eeb', borderRadius: '10px' }} 
                          />
                          {invalidFields.razon_social && (
                            <Form.Control.Feedback type="invalid" className="d-block text-danger fw-bold mt-1">
                              {typeof invalidFields.razon_social === 'string' ? invalidFields.razon_social : 'La Razón Social es obligatoria.'}
                            </Form.Control.Feedback>
                          )}
                        </Form.Group>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-6">
                        <Form.Group className="mb-3">
                          <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>{t('field_commercial_name', 'Nombre Comercial')}</Form.Label>
                          <Form.Control 
                            type="text" 
                            value={formData.nombre_comercial} 
                            onChange={e => setFormData({...formData, nombre_comercial: e.target.value})} 
                            placeholder={t('commercial_name_placeholder', 'Ej: Inversiones El Éxito')}
                            style={{ border: '1px solid #183eeb', borderRadius: '10px' }} 
                          />
                        </Form.Group>
                      </div>
                      <div className="col-md-6">
                        <Form.Group className="mb-3">
                          <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>{t('field_contact_name', 'Persona de Contacto (Nombre y Apellido)')}</Form.Label>
                          <Form.Control 
                            type="text" 
                            value={formData.nombre_contacto} 
                            onChange={e => setFormData({...formData, nombre_contacto: e.target.value})} 
                            placeholder={t('contact_name_placeholder', 'Ej: Juan Pérez')}
                            style={{ border: '1px solid #183eeb', borderRadius: '10px' }} 
                          />
                        </Form.Group>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-6">
                        <Form.Group className="mb-3">
                          <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>{t('field_phone', 'Teléfono')} *</Form.Label>
                          <Form.Control 
                            type="text" 
                            required 
                            isInvalid={!!invalidFields.telefono}
                            value={formData.telefono} 
                            onChange={e => {
                              setFormData({...formData, telefono: e.target.value});
                              if (invalidFields.telefono) setInvalidFields({...invalidFields, telefono: false});
                            }} 
                            style={{ border: invalidFields.telefono ? '2px solid #dc3545' : '1px solid #183eeb', borderRadius: '10px' }} 
                          />
                          {invalidFields.telefono && (
                            <Form.Control.Feedback type="invalid" className="d-block text-danger fw-bold mt-1">
                              {typeof invalidFields.telefono === 'string' ? invalidFields.telefono : 'El Teléfono es obligatorio.'}
                            </Form.Control.Feedback>
                          )}
                        </Form.Group>
                      </div>
                      <div className="col-md-6">
                        <Form.Group className="mb-3">
                          <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>{t('field_billing_email', 'Email de Facturación')}</Form.Label>
                          <Form.Control 
                            type="email" 
                            isInvalid={!!invalidFields.email_facturacion}
                            value={formData.email_facturacion} 
                            onChange={e => {
                              setFormData({...formData, email_facturacion: e.target.value});
                              if (invalidFields.email_facturacion) setInvalidFields({...invalidFields, email_facturacion: false});
                            }} 
                            style={{ border: invalidFields.email_facturacion ? '2px solid #dc3545' : '1px solid #183eeb', borderRadius: '10px' }} 
                          />
                          {invalidFields.email_facturacion && (
                            <Form.Control.Feedback type="invalid" className="d-block text-danger fw-bold mt-1">
                              {typeof invalidFields.email_facturacion === 'string' ? invalidFields.email_facturacion : 'El Email no es válido.'}
                            </Form.Control.Feedback>
                          )}
                        </Form.Group>
                      </div>
                    </div>
                    
                    <Form.Group className="mb-3">
                      <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>{t('field_address', 'Dirección Física')} *</Form.Label>
                      <Form.Control 
                        as="textarea" 
                        rows={2} 
                        required 
                        isInvalid={!!invalidFields.direccion_fisica}
                        value={formData.direccion_fisica} 
                        onChange={e => {
                          setFormData({...formData, direccion_fisica: e.target.value});
                          if (invalidFields.direccion_fisica) setInvalidFields({...invalidFields, direccion_fisica: false});
                        }} 
                        style={{ border: invalidFields.direccion_fisica ? '2px solid #dc3545' : '1px solid #183eeb', borderRadius: '10px' }} 
                      />
                      {invalidFields.direccion_fisica && (
                        <Form.Control.Feedback type="invalid" className="d-block text-danger fw-bold mt-1">
                          {typeof invalidFields.direccion_fisica === 'string' ? invalidFields.direccion_fisica : 'La Dirección Física es obligatoria.'}
                        </Form.Control.Feedback>
                      )}
                      <Form.Text className="text-muted" style={{ fontSize: '0.75rem' }}>
                        {t('address_referential_help', 'Dirección física descriptiva/referencial. Las coordenadas GPS operativas se capturan en tiempo real desde la App Móvil o se editan a continuación.')}
                      </Form.Text>
                    </Form.Group>

                    {/* AVISO DE GEORREFERENCIACIÓN EN CAMPO (APP MÓVIL) */}
                    <div 
                      className="p-3 mb-3 rounded-3 border d-flex align-items-center" 
                      style={{ 
                        backgroundColor: isDark ? 'rgba(24, 62, 235, 0.08)' : '#f8faff', 
                        borderColor: '#93c5fd' 
                      }}
                    >
                      <i className="mdi mdi-cellphone-marker text-primary me-3" style={{ fontSize: '1.8rem' }}></i>
                      <div>
                        <strong className="text-primary small d-block mb-1">
                          Georreferenciación GPS en Campo (Desde la App Móvil)
                        </strong>
                        <span className="text-muted" style={{ fontSize: '0.78rem' }}>
                          El cliente se registrará con su dirección descriptiva referencial. Las coordenadas GPS exactas, la Zona Territorial y el Vendedor Titular definitivo se determinarán automáticamente cuando el cliente valide la ubicación real en el mapa desde la App Móvil.
                        </span>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-3">
                        <Form.Group className="mb-3">
                          <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>{t('field_canal_comercial', 'Canal Comercial')}</Form.Label>
                          <Form.Select 
                            value={formData.canal_comercial} 
                            onChange={e => {
                              const canal = e.target.value;
                              const cfg = getCanalCfg(canal);
                              setFormData(prev => ({
                                ...prev,
                                canal_comercial: canal,
                                subcategoria_comercial: '',
                                limite_credito: cfg ? cfg.limite : prev.limite_credito,
                                dias_gracia: cfg ? cfg.dias_credito : (prev.dias_gracia || 8)
                              }));
                            }} 
                            style={{ border: '1px solid #183eeb', borderRadius: '10px' }}
                          >
                            <option value="">{t('select_channel_placeholder', 'Seleccione canal...')}</option>
                            <option value="transformadores">{t('canal_transformadores', 'Transformadores (Food Service)')} (${getCanalCfg('transformadores')?.limite || 500})</option>
                            <option value="supermercados_bodegones">{t('canal_supermercados_bodegones', 'Supermercados y Bodegones (Retail)')} (${getCanalCfg('supermercados_bodegones')?.limite || 1200})</option>
                            <option value="tiendas_bodegas">{t('canal_tiendas_bodegas', 'Tiendas y Bodegas (Tradicional)')} (${getCanalCfg('tiendas_bodegas')?.limite || 300})</option>
                          </Form.Select>
                        </Form.Group>
                      </div>
                      <div className="col-md-3">
                        <Form.Group className="mb-3">
                          <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>{t('field_subcategoria_rubro', 'Subcategoría / Rubro *')}</Form.Label>
                          <Form.Select 
                            required 
                            isInvalid={!!invalidFields.categoria}
                            value={formData.subcategoria_comercial || formData.categoria} 
                            onChange={e => {
                              const subVal = e.target.value;
                              const canalCfg = CANAL_CONFIG[formData.canal_comercial];
                              const found = canalCfg?.subcategories?.find(s => s.id === subVal || s.defaultLabel === subVal);
                              setFormData(prev => ({
                                ...prev,
                                subcategoria_comercial: found ? found.id : subVal,
                                categoria: found ? found.defaultLabel : subVal
                              }));
                              if (invalidFields.categoria) setInvalidFields({...invalidFields, categoria: false});
                            }} 
                            style={{ border: invalidFields.categoria ? '2px solid #dc3545' : '1px solid #183eeb', borderRadius: '10px' }}
                          >
                            <option value="">{t('select_option', 'Seleccione...')}</option>
                            {formData.canal_comercial && CANAL_CONFIG[formData.canal_comercial] ? (
                              CANAL_CONFIG[formData.canal_comercial].subcategories.map(sub => (
                                <option key={sub.id} value={sub.id}>{t(sub.key, sub.defaultLabel)}</option>
                              ))
                            ) : (
                              <>
                                <optgroup label={t('optgroup_transformadores', 'Transformadores')}>
                                  <option value="restaurantes">{t('subcat_restaurantes', 'Restaurantes')}</option>
                                  <option value="panaderias_pastelerias">{t('subcat_panaderias_pastelerias', 'Panaderías / Pastelerías')}</option>
                                  <option value="pizzerias">{t('subcat_pizzerias', 'Pizzerías')}</option>
                                  <option value="hoteles">{t('subcat_hoteles', 'Hoteles')}</option>
                                  <option value="food_service">{t('subcat_food_service', 'Food Service')}</option>
                                </optgroup>
                                <optgroup label={t('optgroup_retail', 'Retail / Consumo Masivo')}>
                                  <option value="supermercados">{t('subcat_supermercados', 'Supermercados')}</option>
                                  <option value="hipermercados">{t('subcat_hipermercados', 'Hipermercados')}</option>
                                  <option value="bodegones">{t('subcat_bodegones', 'Bodegones')}</option>
                                </optgroup>
                                <optgroup label={t('optgroup_tradicional', 'Canal Tradicional')}>
                                  <option value="abastos">{t('subcat_abastos', 'Abastos')}</option>
                                  <option value="tiendas_conveniencia">{t('subcat_tiendas_conveniencia', 'Tiendas de conveniencia')}</option>
                                  <option value="Bodega">{t('subcat_bodega', 'Bodega')}</option>
                                </optgroup>
                              </>
                            )}
                          </Form.Select>
                          {invalidFields.categoria && (
                            <Form.Control.Feedback type="invalid" className="d-block text-danger fw-bold mt-1">
                              {typeof invalidFields.categoria === 'string' ? invalidFields.categoria : t('field_subcategoria_required', 'La Subcategoría es obligatoria.')}
                            </Form.Control.Feedback>
                          )}
                        </Form.Group>
                      </div>
                      <div className="col-md-3">
                        <Form.Group className="mb-3">
                          <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>{t('field_credit_limit_label', 'Límite de Compra / Crédito ($)')}</Form.Label>
                          <Form.Control 
                            type="number" 
                            step="0.01" 
                            min="0" 
                            isInvalid={!!invalidFields.limite_credito}
                            value={formData.limite_credito} 
                            onChange={e => {
                              setFormData({...formData, limite_credito: e.target.value});
                              if (invalidFields.limite_credito) setInvalidFields({...invalidFields, limite_credito: false});
                            }} 
                            style={{ border: invalidFields.limite_credito ? '2px solid #dc3545' : '1px solid #183eeb', borderRadius: '10px' }} 
                          />
                        </Form.Group>
                      </div>
                      <div className="col-md-3">
                        <Form.Group className="mb-3">
                          <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>{t('field_dias_credito', 'Días de Crédito')}</Form.Label>
                          <Form.Control 
                            type="number" 
                            min="1" 
                            max="180" 
                            value={formData.dias_gracia ?? 8} 
                            onChange={e => {
                              setFormData({ ...formData, dias_gracia: parseInt(e.target.value, 10) || 0 });
                            }} 
                            style={{ border: '1px solid #183eeb', borderRadius: '10px' }} 
                          />
                          <Form.Text className="text-muted" style={{ fontSize: '0.72rem' }}>
                            {t('dias_credito_help', 'Plazo de gracia por factura')}
                          </Form.Text>
                        </Form.Group>
                      </div>
                    </div>

                    {/* DOCUMENT ATTACHMENTS (RIF, CÉDULA, MERCANTIL) WITH ORIGINAL INTERACTIVE FILE PILLS */}
                    <div className="row">
                      <div className="col-md-12">
                        <Form.Group className="mb-3">
                          <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>
                            <i className="mdi mdi-paperclip me-1"></i> {t('field_documents', 'Documentos / Imágenes Adjuntas (RIF, Cédula, Registro - Opcional)')}
                          </Form.Label>
                          <div>
                            <input 
                              type="file" 
                              id="manual-customer-documents-upload"
                              multiple 
                              accept=".pdf,.jpg,.jpeg,.png" 
                              onChange={handleFileSelect} 
                              className="d-none" 
                            />
                            <Button 
                              as="label" 
                              htmlFor="manual-customer-documents-upload" 
                              className="d-inline-flex align-items-center shadow-sm"
                              style={{ borderRadius: '10px', fontWeight: '600', cursor: 'pointer', padding: '8px 16px', backgroundColor: '#f4f6fd', borderColor: '#183eeb', color: '#183eeb' }}
                            >
                              <i className="mdi mdi-file-plus-outline me-2" style={{ fontSize: '1.2rem' }}></i>
                              {documentFiles.length > 0 ? t('add_more_documents_btn', 'Agregar Más Documentos') : t('select_documents', 'Seleccionar Documentos')}
                            </Button>
                          </div>
                          <Form.Text className="text-muted d-block mt-1">
                            {t('documents_help', 'Formatos soportados: PDF, JPG, PNG (máx. 10MB). Puedes seleccionar múltiples archivos o agregarlos uno a uno.')}
                          </Form.Text>

                          {documentFiles.length > 0 && (
                            <div className="mt-2 p-3 border rounded" style={{ backgroundColor: '#f8f9fa', borderRadius: '10px' }}>
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <small className="fw-bold text-primary" style={{ fontSize: '0.9rem' }}>
                                  <i className="mdi mdi-file-document-multiple-outline me-1"></i>
                                  {t('selected_files_count', 'Archivos Seleccionados')} ({documentFiles.length}):
                                </small>
                                <Button 
                                  variant="outline-danger" 
                                  size="sm" 
                                  onClick={() => setDocumentFiles([])}
                                  style={{ borderRadius: '8px', padding: '3px 10px', fontSize: '0.78rem', fontWeight: '600' }}
                                >
                                  <i className="mdi mdi-close-circle me-1"></i> {t('clear_all_files', 'Desmarcar Todos')}
                                </Button>
                              </div>
                              <div className="d-flex flex-wrap gap-2">
                                {documentFiles.map((file, idx) => (
                                  <span key={idx} className="badge bg-white text-dark border p-2 d-inline-flex align-items-center shadow-sm" style={{ borderRadius: '8px', fontSize: '0.85rem', fontWeight: 'normal' }}>
                                    <i className="mdi mdi-paperclip text-primary me-1" style={{ fontSize: '1rem' }}></i>
                                    <span className="fw-bold me-1">{file.name}</span>
                                    <span className="text-muted small">({(file.size / 1024).toFixed(0)} KB)</span>
                                    <button 
                                      type="button" 
                                      className="btn-close ms-2" 
                                      aria-label="Remove" 
                                      onClick={() => setDocumentFiles(documentFiles.filter((_, i) => i !== idx))}
                                      style={{ fontSize: '0.7rem' }}
                                    ></button>
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </Form.Group>
                      </div>
                    </div>
                  </div>
                </Tab>
                <Tab eventKey="import" title={<><i className="mdi mdi-cloud-upload me-1"></i> {t('tab_bulk_import', 'Carga Masiva de Clientes')}</>}>
                  <div className="p-3">
                    <div className="alert alert-info d-flex align-items-center mb-3" style={{ borderRadius: '10px' }}>
                      <i className="mdi mdi-information-outline me-2" style={{ fontSize: '1.4rem' }}></i>
                      <div>
                        <strong>{t('import_alert_title', 'Migración y Pre-activación Masiva')}</strong><br/>
                        {t('import_alert_notice', 'Sube un archivo Excel (.xlsx) o CSV con RIF, Razón Social, Nombre Comercial, Dirección Fiscal, Teléfono, Email de Facturación, Categoría, Límite de Crédito y Vendedor asignado.')}
                      </div>
                    </div>

                    {/* ORIGINAL TEMPLATE DOWNLOAD CONTAINER */}
                    <div 
                      className="d-flex justify-content-between align-items-center mb-3 p-3 rounded" 
                      style={{ 
                        backgroundColor: isDark ? '#10172d' : '#f8fafc', 
                        border: isDark ? '1px solid #233052' : '1px solid #e3e6f0',
                        color: isDark ? '#f8fafc' : '#010028'
                      }}
                    >
                      <div>
                        <h6 className="mb-1 font-weight-bold" style={{ color: isDark ? '#60a5fa' : '#183eeb' }}>
                          <i className="mdi mdi-file-excel-box me-1"></i> {t('download_template_title', '¿No tienes el formato oficial?')}
                        </h6>
                        <small style={{ color: isDark ? '#94a3b8' : '#64748b' }}>{t('download_template_sub', 'Descarga la plantilla estándar completa con los 9 campos del registro de clientes y filas de ejemplo.')}</small>
                      </div>
                      <Button 
                        variant="outline-success" 
                        size="sm" 
                        onClick={handleDownloadTemplate} 
                        className="d-flex align-items-center fw-bold shadow-sm"
                        style={{ borderRadius: '10px', padding: '8px 14px' }}
                      >
                        <i className="mdi mdi-download me-1" style={{ fontSize: '1.2rem' }}></i>
                        {t('download_template_btn', 'Descargar Plantilla (.xlsx)')}
                      </Button>
                    </div>

                    {/* ORIGINAL INTERACTIVE DRAG-AND-DROP DROPZONE */}
                    <Form.Group 
                      className="mb-3 text-center p-4 border-dashed" 
                      style={{ 
                        border: isDragging ? '2px dashed #183eeb' : '2px dashed #b4c6ff', 
                        borderRadius: '15px', 
                        backgroundColor: isDragging ? '#e8eeff' : '#f4f6fd',
                        transition: 'all 0.2s ease-in-out'
                      }}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                    >
                      <i className="mdi mdi-cloud-upload-outline d-block mb-2" style={{ fontSize: '48px', color: '#183eeb' }}></i>
                      <Form.Label style={{ color: '#010028', fontWeight: 'bold', fontSize: '1.1rem', cursor: 'pointer' }}>
                        {t('drop_file_label', 'Arrastra y suelta tu archivo aquí o haz clic para examinar')}
                      </Form.Label>
                      <Form.Control 
                        type="file" 
                        accept=".xlsx,.xls,.csv" 
                        required={modalTab === 'import'} 
                        onChange={e => {
                          if (e.target.files && e.target.files.length > 0) {
                            setImportFile(e.target.files[0]);
                          }
                        }} 
                        className="d-none"
                        id="excel-upload"
                      />
                      <div>
                        <Button as="label" htmlFor="excel-upload" style={{ backgroundColor: '#183eeb', borderColor: '#183eeb', color: 'white', borderRadius: '10px' }}>
                          {t('browse_files', 'Examinar Archivos')}
                        </Button>
                      </div>
                      {importFile && (
                        <div className="mt-3 text-success fw-bold p-2 bg-white rounded border d-inline-block shadow-sm">
                          <i className="mdi mdi-file-excel text-success me-1" style={{ fontSize: '1.2rem' }}></i>
                          <span>{importFile.name}</span>
                          <span className="text-muted small ms-2">({(importFile.size / 1024).toFixed(1)} KB)</span>
                        </div>
                      )}
                    </Form.Group>
                  </div>
                </Tab>
              </Tabs>
            ) : (
              <div className="p-3">
                <div className="row">
                  <div className="col-md-6">
                    <Form.Group className="mb-3">
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <Form.Label className="mb-0" style={{ color: '#010028', fontWeight: 'bold' }}>{t('field_rif', 'RIF o Cédula')} *</Form.Label>
                        {isEditingValidatedCustomer ? (
                          <Badge bg="secondary" className="small" style={{ fontSize: '0.72rem' }}>
                            <i className="mdi mdi-lock me-1"></i>{t('badge_locked', 'Bloqueado (Validado)')}
                          </Badge>
                        ) : (
                          <Badge bg="info" className="small text-white" style={{ fontSize: '0.72rem' }}>
                            <i className="mdi mdi-pencil me-1"></i>{t('badge_editable_not_validated', 'Editable (No Validado)')}
                          </Badge>
                        )}
                      </div>
                      <Form.Control 
                        type="text" 
                        required 
                        disabled={isEditingValidatedCustomer} 
                        isInvalid={!!invalidFields.rif_cedula}
                        value={formData.rif_cedula} 
                        onChange={e => {
                          setFormData({...formData, rif_cedula: e.target.value});
                          if (invalidFields.rif_cedula) setInvalidFields({...invalidFields, rif_cedula: false});
                        }} 
                        onBlur={handleRifBlur}
                        placeholder="J-12345678-9"
                        style={{ 
                          border: invalidFields.rif_cedula ? '2px solid #dc3545' : '1px solid #183eeb', 
                          borderRadius: '10px',
                          backgroundColor: isEditingValidatedCustomer ? (isDark ? '#233052' : '#e9ecef') : (isDark ? '#1a233a' : '#ffffff'),
                          cursor: isEditingValidatedCustomer ? 'not-allowed' : 'text'
                        }} 
                      />
                      {invalidFields.rif_cedula && (
                        <Form.Control.Feedback type="invalid" className="d-block text-danger fw-bold mt-1">
                          {typeof invalidFields.rif_cedula === 'string' ? invalidFields.rif_cedula : 'El RIF o Cédula es obligatorio.'}
                        </Form.Control.Feedback>
                      )}
                      <Form.Text className={isEditingValidatedCustomer ? "text-muted" : "text-primary fw-bold"} style={{ fontSize: '0.75rem' }}>
                        {isEditingValidatedCustomer 
                          ? t('rif_locked_notice', 'El RIF no puede modificarse porque el cliente ya fue validado y activado.')
                          : t('rif_editable_notice', 'Puedes editar el RIF libremente ya que el cliente aún no ha sido validado en el sistema.')}
                      </Form.Text>
                    </Form.Group>
                  </div>
                  <div className="col-md-6">
                    <Form.Group className="mb-3">
                      <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>{t('field_razon_social', 'Razón Social')} *</Form.Label>
                      <Form.Control type="text" required value={formData.razon_social} onChange={e => setFormData({...formData, razon_social: e.target.value})} style={{ border: '1px solid #183eeb', borderRadius: '10px' }} />
                    </Form.Group>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    <Form.Group className="mb-3">
                      <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>{t('field_commercial_name', 'Nombre Comercial')}</Form.Label>
                      <Form.Control 
                        type="text" 
                        value={formData.nombre_comercial} 
                        onChange={e => setFormData({...formData, nombre_comercial: e.target.value})} 
                        placeholder={t('commercial_name_placeholder', 'Ej: Inversiones El Éxito')}
                        style={{ border: '1px solid #183eeb', borderRadius: '10px' }} 
                      />
                    </Form.Group>
                  </div>
                  <div className="col-md-6">
                    <Form.Group className="mb-3">
                      <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>{t('field_contact_name', 'Persona de Contacto (Nombre y Apellido)')}</Form.Label>
                      <Form.Control 
                        type="text" 
                        value={formData.nombre_contacto} 
                        onChange={e => setFormData({...formData, nombre_contacto: e.target.value})} 
                        placeholder={t('contact_name_placeholder', 'Ej: Juan Pérez')}
                        style={{ border: '1px solid #183eeb', borderRadius: '10px' }} 
                      />
                    </Form.Group>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    <Form.Group className="mb-3">
                      <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>{t('field_phone', 'Teléfono')} *</Form.Label>
                      <Form.Control type="text" required value={formData.telefono} onChange={e => setFormData({...formData, telefono: e.target.value})} style={{ border: '1px solid #183eeb', borderRadius: '10px' }} />
                    </Form.Group>
                  </div>
                  <div className="col-md-6">
                    <Form.Group className="mb-3">
                      <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>{t('field_billing_email', 'Email de Facturación')}</Form.Label>
                      <Form.Control type="email" value={formData.email_facturacion} onChange={e => setFormData({...formData, email_facturacion: e.target.value})} style={{ border: '1px solid #183eeb', borderRadius: '10px' }} />
                    </Form.Group>
                  </div>
                </div>
                
                <Form.Group className="mb-3">
                  <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>{t('field_address', 'Dirección Física')} *</Form.Label>
                  <Form.Control as="textarea" rows={2} required value={formData.direccion_fisica} onChange={e => setFormData({...formData, direccion_fisica: e.target.value})} style={{ border: '1px solid #183eeb', borderRadius: '10px' }} />
                  <Form.Text className="text-muted" style={{ fontSize: '0.75rem' }}>
                    {t('address_referential_help', 'Dirección física descriptiva/referencial. Las coordenadas GPS operativas se capturan en tiempo real desde la App Móvil o se editan a continuación.')}
                  </Form.Text>
                </Form.Group>

                {/* PANEL DE COORDENADAS GEOGRÁFICAS GPS EN EDICIÓN */}
                <div 
                  className="p-3 mb-3 rounded-3 border" 
                  style={{ 
                    backgroundColor: isDark ? 'rgba(24, 62, 235, 0.08)' : '#f8faff', 
                    borderColor: '#183eeb' 
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="fw-bold small" style={{ color: '#183eeb' }}>
                      <i className="mdi mdi-crosshairs-gps me-1"></i> Coordenadas Geográficas GPS (Fuente de la Verdad Territorial)
                    </span>
                    {formData.latitude && formData.longitude && (
                      <a 
                        href={`https://www.google.com/maps/search/?api=1&query=${formData.latitude},${formData.longitude}`} 
                        target="_blank" 
                        rel="noreferrer" 
                        className="btn btn-xs btn-outline-primary py-0 px-2"
                        style={{ fontSize: '0.74rem' }}
                      >
                        <i className="mdi mdi-map-marker-radius me-1"></i>Ver en Google Maps
                      </a>
                    )}
                  </div>
                  <div className="row g-2">
                    <div className="col-md-6">
                      <Form.Group className="mb-1">
                        <Form.Label className="small fw-bold text-muted mb-1">Latitud GPS</Form.Label>
                        <Form.Control 
                          type="text" 
                          placeholder="Ej: 8.627500 o pegar '8.62, -70.21'"
                          value={formData.latitude}
                          onChange={e => {
                            const val = e.target.value;
                            const parts = val.trim().split(/[,\s;]+/);
                            if (parts.length >= 2 && !isNaN(parts[0]) && !isNaN(parts[1]) && parts[0] !== '' && parts[1] !== '') {
                              setFormData(prev => ({
                                ...prev,
                                latitude: parts[0],
                                longitude: parts[1],
                                ubicacion_gps: `${parts[0]},${parts[1]}`
                              }));
                            } else {
                              setFormData(prev => ({
                                ...prev,
                                latitude: val,
                                ubicacion_gps: val && prev.longitude ? `${val},${prev.longitude}` : prev.ubicacion_gps
                              }));
                            }
                          }}
                          style={{ borderRadius: '8px', border: '1px solid #93c5fd', fontSize: '0.85rem' }}
                        />
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group className="mb-1">
                        <Form.Label className="small fw-bold text-muted mb-1">Longitud GPS</Form.Label>
                        <Form.Control 
                          type="text" 
                          placeholder="Ej: -70.208500"
                          value={formData.longitude}
                          onChange={e => {
                            const lng = e.target.value;
                            setFormData(prev => ({
                              ...prev,
                              longitude: lng,
                              ubicacion_gps: prev.latitude && lng ? `${prev.latitude},${lng}` : prev.ubicacion_gps
                            }));
                          }}
                          style={{ borderRadius: '8px', border: '1px solid #93c5fd', fontSize: '0.85rem' }}
                        />
                      </Form.Group>
                    </div>
                  </div>
                  <div className="text-muted mt-2" style={{ fontSize: '0.72rem' }}>
                    <i className="mdi mdi-information-outline me-1 text-primary"></i>
                    Al modificar las coordenadas GPS, el backend reubica automáticamente al cliente en su <strong>Zona Geográfica</strong> y le reasigna el <strong>Vendedor Titular</strong> correspondiente a esa geocerca.
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-3">
                    <Form.Group className="mb-3">
                      <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>{t('field_canal_comercial', 'Canal Comercial')}</Form.Label>
                      <Form.Select 
                        value={formData.canal_comercial} 
                        onChange={e => {
                          const canal = e.target.value;
                          const cfg = getCanalCfg(canal);
                          setFormData(prev => ({
                            ...prev,
                            canal_comercial: canal,
                            subcategoria_comercial: '',
                            limite_credito: cfg ? cfg.limite : prev.limite_credito,
                            dias_gracia: cfg ? cfg.dias_credito : (prev.dias_gracia || 8)
                          }));
                        }} 
                        style={{ border: '1px solid #183eeb', borderRadius: '10px' }}
                      >
                        <option value="">{t('select_channel_placeholder', 'Seleccione canal...')}</option>
                        <option value="transformadores">{t('canal_transformadores', 'Transformadores (Food Service)')} (${getCanalCfg('transformadores')?.limite || 500})</option>
                        <option value="supermercados_bodegones">{t('canal_supermercados_bodegones', 'Supermercados y Bodegones (Retail)')} (${getCanalCfg('supermercados_bodegones')?.limite || 1200})</option>
                        <option value="tiendas_bodegas">{t('canal_tiendas_bodegas', 'Tiendas y Bodegas (Tradicional)')} (${getCanalCfg('tiendas_bodegas')?.limite || 300})</option>
                      </Form.Select>
                    </Form.Group>
                  </div>

                  <div className="col-md-3">
                    <Form.Group className="mb-3">
                      <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>{t('field_subcategoria_rubro', 'Subcategoría / Rubro *')}</Form.Label>
                      <Form.Select 
                        required 
                        value={formData.subcategoria_comercial || formData.categoria} 
                        onChange={e => {
                          const subVal = e.target.value;
                          const canalCfg = CANAL_CONFIG[formData.canal_comercial];
                          const found = canalCfg?.subcategories?.find(s => s.id === subVal || s.defaultLabel === subVal);
                          setFormData(prev => ({
                            ...prev,
                            subcategoria_comercial: found ? found.id : subVal,
                            categoria: found ? found.defaultLabel : subVal
                          }));
                        }} 
                        style={{ border: '1px solid #183eeb', borderRadius: '10px' }}
                      >
                        <option value="">{t('select_option', 'Seleccione...')}</option>
                        {formData.canal_comercial && CANAL_CONFIG[formData.canal_comercial] ? (
                          CANAL_CONFIG[formData.canal_comercial].subcategories.map(sub => (
                            <option key={sub.id} value={sub.id}>{t(sub.key, sub.defaultLabel)}</option>
                          ))
                        ) : (
                          <>
                            <optgroup label={t('optgroup_transformadores', 'Transformadores')}>
                              <option value="restaurantes">{t('subcat_restaurantes', 'Restaurantes')}</option>
                              <option value="panaderias_pastelerias">{t('subcat_panaderias_pastelerias', 'Panaderías / Pastelerías')}</option>
                              <option value="pizzerias">{t('subcat_pizzerias', 'Pizzerías')}</option>
                              <option value="hoteles">{t('subcat_hoteles', 'Hoteles')}</option>
                              <option value="food_service">{t('subcat_food_service', 'Food Service')}</option>
                            </optgroup>
                            <optgroup label={t('optgroup_retail', 'Retail / Consumo Masivo')}>
                              <option value="supermercados">{t('subcat_supermercados', 'Supermercados')}</option>
                              <option value="hipermercados">{t('subcat_hipermercados', 'Hipermercados')}</option>
                              <option value="bodegones">{t('subcat_bodegones', 'Bodegones')}</option>
                            </optgroup>
                            <optgroup label={t('optgroup_tradicional', 'Canal Tradicional')}>
                              <option value="abastos">{t('subcat_abastos', 'Abastos')}</option>
                              <option value="tiendas_conveniencia">{t('subcat_tiendas_conveniencia', 'Tiendas de conveniencia')}</option>
                              <option value="Bodega">{t('subcat_bodega', 'Bodega')}</option>
                            </optgroup>
                          </>
                        )}
                      </Form.Select>
                    </Form.Group>
                  </div>

                  <div className="col-md-3">
                    <Form.Group className="mb-3">
                      <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>{t('field_credit_limit_label', 'Límite de Compra / Crédito ($)')}</Form.Label>
                      <Form.Control 
                        type="number" 
                        step="0.01" 
                        min="0" 
                        value={formData.limite_credito} 
                        onChange={e => setFormData({...formData, limite_credito: e.target.value})} 
                        style={{ border: '1px solid #183eeb', borderRadius: '10px' }} 
                      />
                    </Form.Group>
                  </div>

                  <div className="col-md-3">
                    <Form.Group className="mb-3">
                      <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>{t('field_dias_credito', 'Días de Crédito')}</Form.Label>
                      <Form.Control 
                        type="number" 
                        min="1" 
                        max="180" 
                        value={formData.dias_gracia ?? 8} 
                        onChange={e => setFormData({ ...formData, dias_gracia: parseInt(e.target.value, 10) || 0 })} 
                        style={{ border: '1px solid #183eeb', borderRadius: '10px' }} 
                      />
                      <Form.Text className="text-muted" style={{ fontSize: '0.72rem' }}>
                        {t('dias_credito_help', 'Plazo de gracia por factura')}
                      </Form.Text>
                    </Form.Group>
                  </div>
                </div>

                <div className="row g-3 mb-3">
                  <div className="col-md-6">
                    <Form.Group>
                      <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>
                        <i className="mdi mdi-map-marker-outline me-1"></i> {t('field_zone', 'Zona Territorial')}
                        <span className="badge bg-light text-muted border ms-2" style={{ fontSize: '0.7rem' }}>
                          <i className="mdi mdi-lock me-1"></i>{t('readonly_label', 'Solo Lectura')}
                        </span>
                      </Form.Label>
                      {(() => {
                        const targetZoneId = formData.zone_id || editingCustomer?.zone_id;
                        const matchedZone = zones.find(z => String(z.id) === String(targetZoneId)) || editingCustomer?.zone;
                        const zoneLabel = matchedZone 
                          ? `${matchedZone.name} (${matchedZone.code || 'Zona'})` 
                          : t('no_zone_assigned', 'Sin Zona Asignada (Requiere Coordenadas GPS)');
                        return (
                          <Form.Control
                            type="text"
                            readOnly
                            disabled
                            value={zoneLabel}
                            style={{ 
                              backgroundColor: '#f1f5f9', 
                              border: '1px solid #cbd5e1', 
                              color: matchedZone ? '#1e293b' : '#64748b', 
                              fontWeight: 500, 
                              borderRadius: '10px' 
                            }}
                          />
                        );
                      })()}
                      <Form.Text className="text-muted" style={{ fontSize: '0.72rem' }}>
                        <i className="mdi mdi-information-outline me-1"></i>
                        {t('zone_readonly_help', 'Determinada automáticamente por la geocerca de las coordenadas GPS.')}
                      </Form.Text>
                    </Form.Group>
                  </div>
                  <div className="col-md-6">
                    <Form.Group>
                      <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>
                        <i className="mdi mdi-account-tie me-1"></i> {t('field_assigned_seller', 'Vendedor Asignado')}
                        <span className="badge bg-light text-muted border ms-2" style={{ fontSize: '0.7rem' }}>
                          <i className="mdi mdi-lock me-1"></i>{t('readonly_label', 'Solo Lectura')}
                        </span>
                      </Form.Label>
                      {(() => {
                        const targetVendorId = formData.vendedor_id || editingCustomer?.vendedor_id;
                        const matchedVendor = vendors.find(v => String(v.id) === String(targetVendorId)) || editingCustomer?.vendedor;
                        const vendorLabel = matchedVendor 
                          ? (matchedVendor.nombre_completo || `${matchedVendor.name || ''} ${matchedVendor.lastname || ''}`.trim() || matchedVendor.email) 
                          : t('no_vendor_assigned', 'Sin Vendedor Titular');
                        return (
                          <Form.Control
                            type="text"
                            readOnly
                            disabled
                            value={vendorLabel}
                            style={{ 
                              backgroundColor: '#f1f5f9', 
                              border: '1px solid #cbd5e1', 
                              color: matchedVendor ? '#1e293b' : '#64748b', 
                              fontWeight: 500, 
                              borderRadius: '10px' 
                            }}
                          />
                        );
                      })()}
                      <Form.Text className="text-muted" style={{ fontSize: '0.72rem' }}>
                        <i className="mdi mdi-information-outline me-1"></i>
                        {t('vendor_readonly_help', 'Titular comercial asignado a la zona en el Maestro de Zonas.')}
                      </Form.Text>
                    </Form.Group>
                  </div>
                </div>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer style={{ backgroundColor: '#f8f9fa', borderTop: '2px solid #00024f' }}>
            <Button variant="outline-secondary" onClick={() => setShowModal(false)} style={{ color: '#010028', borderColor: '#010028', borderRadius: '10px' }}>{t('btn_cancel', 'Cancelar')}</Button>
            <Button type="submit" disabled={processing} style={{ backgroundColor: '#183eeb', borderColor: '#183eeb', color: 'white', borderRadius: '10px' }}>
              {processing ? <Spinner size="sm" animation="border" /> : ((editingCustomer || editingId) ? t('btn_save_changes', 'Guardar Cambios') : (modalTab === 'import' ? t('btn_start_import', 'Iniciar Importación') : t('btn_save_customer', 'Guardar Cliente')))}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* BULK IMPORT ERROR REPORT MODAL (ORIGINAL HU09 DESIGN) */}
      <Modal show={showImportErrorModal} onHide={() => setShowImportErrorModal(false)} size="lg" centered>
        <Modal.Header closeButton closeVariant="white" style={{ backgroundColor: '#dc3545', color: '#ffffff', borderBottom: '3px solid #b02a37' }}>
          <Modal.Title className="d-flex align-items-center">
            <i className="mdi mdi-alert-rhombus me-2" style={{ fontSize: '1.5rem' }}></i>
            {t('modal_error_report_title', 'Reporte de Errores en Carga Masiva')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <Alert variant="danger" className="d-flex align-items-center mb-4" style={{ borderRadius: '10px' }}>
            <i className="mdi mdi-alert-circle me-3" style={{ fontSize: '2rem' }}></i>
            <div>
              <strong className="d-block mb-1">{t('import_rejected_title', 'La importación fue rechazada por completo (Rollback Transaccional)')}</strong>
              <span>{t('import_rejected_sub', 'Se detectaron fallos de validación en las filas detalladas a continuación. Ningún cliente fue guardado en el sistema.')}</span>
            </div>
          </Alert>

          <div className="table-responsive" style={{ maxHeight: '350px', overflowY: 'auto' }}>
            <Table striped bordered hover size="sm" className="align-middle mb-0">
              <thead className="sticky-top">
                <tr>
                  <th className="text-center" style={{ width: '80px' }}>{t('th_row', 'Fila #')}</th>
                  <th style={{ width: '180px' }}>{t('th_field', 'Campo')}</th>
                  <th>{t('th_reason', 'Motivo del Error')}</th>
                </tr>
              </thead>
              <tbody>
                {importErrors.map((err, idx) => (
                  <tr key={idx}>
                    <td className="text-center fw-bold text-danger">
                      <Badge bg="danger" style={{ fontSize: '0.85rem' }}>{err.row || err.fila || '-'}</Badge>
                    </td>
                    <td className="fw-bold text-dark">{err.field || err.campo || 'General'}</td>
                    <td className="text-danger font-weight-bold">{err.reason || err.motivo || err.message}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Modal.Body>
        <Modal.Footer style={{ backgroundColor: isDark ? '#10172d' : '#f8f9fc', borderTop: isDark ? '1px solid #233052' : '1px solid #dee2e6' }}>
          <Button 
            variant="secondary" 
            onClick={() => { setShowImportErrorModal(false); setShowModal(true); setModalTab('import'); }}
            style={{ borderRadius: '10px' }}
          >
            <i className="mdi mdi-refresh me-1"></i> {t('btn_fix_and_retry', 'Corregir Archivo y Reintentar')}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={!!previewImage}
        onHide={() => setPreviewImage(null)}
        size="lg"
        centered
        style={{ zIndex: 1080 }}
        onExited={() => {
          if (showInspectModal || showVisitModal || showApproveModal || showRejectModal) {
            document.body.classList.add('modal-open');
          }
        }}
      >
        <Modal.Header 
          closeButton 
          closeVariant={isDark ? "white" : undefined} 
          style={{ 
            backgroundColor: isDark ? '#161e38' : '#f8fafc', 
            borderBottom: isDark ? '1px solid #233052' : '1px solid #e2e8f0' 
          }}
        >
          <Modal.Title className="fs-6 fw-bold" style={{ color: isDark ? '#ffffff' : '#010028' }}>
            <i className="mdi mdi-image-search-outline me-2 text-primary"></i>
            {previewImage?.title || 'Vista Ampliada'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-2 text-center" style={{ backgroundColor: isDark ? '#050a17' : '#0f172a' }}>
          {previewImage && (
            <img
              src={previewImage.url}
              alt="Vista previa ampliada"
              className="img-fluid rounded"
              style={{ maxHeight: '78vh', objectFit: 'contain' }}
            />
          )}
        </Modal.Body>
      </Modal>
    </>
  );
}

export default Customerlisting;
